import type { IRenderer } from 'pixi.js';
import * as PIXI from 'pixi.js';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import type {
    TranscriptWordData,
    OpaqueVideoData,
    RawTemplateData,
    RawVideoData,
    TranscriptData,
    RawSceneData,
} from './video-data/types';
import { query } from './query';
import { assetNameComponentsFromName, assetNameFromNameComponents, hasOwnProperty } from './common';

import {
    getReferenceResolution,
    getResolution,
    getResolutionValueFromVideoHeight,
} from './libs/videoData';
import {
    SceneImageRenderMode,
    BufferingMode,
    SeekMode,
    LicenseEnum,
    DynamicStylingCreationLevel,
    DynamicLevel,
    WatermarkPositions,
    TextDirection,
    TextAlignment,
    GraphicClassTypes,
    DynamicInferredHeight,
    AssetCreatedFrom,
    AssetSuffix,
} from './enums';
import type {
    RenderSceneImageResult,
    RenderSceneImageTask,
    TaskCallbackOptions,
} from './libs/RenderQueue';
import { DEFAULT_SCENE_IMAGE_RENDER_MODE, RenderQueue } from './libs/RenderQueue';
import { TimeAwareBase } from './core/TimeAwareBase';
import { Scene } from './core/scene/Scene';
import type { SceneSettings } from './core/scene/Scene';
import type { SfxResult } from './audio/sfx-types';
import { AssetSfxEvent, SceneSfxEvent } from './audio/sfx-types';
import { OfflineAudioEncoder } from './encoders/OfflineAudioEncoder';
import { TickerTimeline } from './libs/TickerTimeline';
import { Config } from './video-data/config';
import { MorphManager } from './core/morph/MorphManager';
import { VideoData } from './video-data';
import { Migrator, VERSION } from './video-data/migrations/migrator';
import { AspectRatio, ALL_ASPECT_RATIOS } from './aspectRatioConfig';

import type { AddSceneSettings } from './core/scene/types';
import type { TraverseScenesCallback } from './core/SceneGroupManager';
import { SceneGroupManager } from './core/SceneGroupManager';
import type { SceneGroup } from './core/SceneGroup';
import { CanvasReusableTexturesManager } from './canvasReusableTexuresManager';

import { Template } from './core/template/Template';
import type { Template as TemplateType } from './core/template/Template';
import { ResourcesStats } from './resourcesStats';
import {
    assertBoolean,
    assertInArray,
    assertInEnum,
    assertIsValidAudio,
    assertIsValidTemplateBundle,
    assertKeyInObject,
    assertMaximum,
    assertMinimum,
    assertNumber,
    assertNumberBetween,
    assertObject,
    assertString,
    assertUuid,
} from './validators';
import { Capabilities } from './core/Capabilities';
import uuid from './video-data/utils/uuid';
import { getBrandSceneDesignConfig } from './video-data/designs/scenes';
import { getGlobalFontManager } from './core/FontManager';
import { ConcurrentTimeline } from './libs/ConcurrentTimeline';

import LuminaryActivityProfiler from './profiling/LuminaryActivityProfiler';
import { METRIC_LOAD, METRIC_RENDER_WORKSPACE, METRIC_SAVE } from './profiling/metrics';
import type {
    FetchResources,
    MediaSize,
    Milliseconds,
    Resolution,
    SceneCategory,
    TemplateBundle,
    TextEditingOptions,
    SceneTemplateReferenceMap,
    AlignChildren,
    LuminaryObject,
    BoundingBox,
    AssetTemplateReferenceMap,
    LifecycleAware,
} from './types';
import type { AudioSourceData, AudioAsset as AudioAssetType } from './audio/AudioAsset';
import { AudioAsset } from './audio/AudioAsset';
import { AudioAnimationManager } from './audio/AudioAnimationManager';
import { Volumes } from './audio/types';
import { cloneDeep } from './libs/cloneDeep';
import type {
    Asset,
    AssetType,
    GetAssetsOptions,
    GetAssetType,
    CopyPasteData,
} from './core/asset/BaseAsset';
import { BaseAsset } from './core/asset/BaseAsset';
import type { ValidationResult } from './stateValidators';
import { validateUniqueMediaAssetIds, validateAspectRatioConsistency } from './stateValidators';
import { FrameByFrameRenderer } from './frameByFrameRenderer/Renderer';
import type { RenderFlags } from './frameByFrameRenderer/Renderer';
import { FAST_WEBGL, isWebGLSupported, SLOW_WEBGL } from './webGLSupported';
import { Subscene } from './core/scene/Subscene';
import { MediaAsset, type MediaSourceData } from './core/asset/MediaAsset';
import { IconAsset } from './core/asset/IconAsset';
import type { IconSourceData } from './core/asset/IconAsset';
import { AssetGroup } from './core/asset/AssetGroup';
import { RectangleAsset } from './core/asset/RectangleAsset';
import { TimeAwareMediaAsset } from './core/asset/TimeAwareMediaAsset';
import { FancyCaptionAsset } from './core/asset/FancyCaptionAsset/asset/FancyCaptionAsset';
import type { TemplateColor, TemplateWatermarkSource } from './core/template/types';
import type { TextAssetDynamicAnimation } from './core/asset/TextAsset/asset/TextAssetDynamicAnimation';
import type { TextAssetDynamicGraphic } from './core/asset/TextAsset/asset/TextAssetDynamicGraphic';
import type { TextAssetDynamicBox } from './core/asset/TextAsset/asset/TextAssetDynamicBox';
import type { TextAssetDynamicBoxBorder } from './core/asset/TextAsset/asset/TextAssetDynamicBoxBorder';
import { TextAssetStyle } from './core/asset/TextAsset/TextAssetStyle';
import { DynamicAnimationConfiguration } from './libs/DynamicAnimationConfiguration';
import { DynamicGraphicsConfigurator } from './libs/DynamicGraphicsConfigurator';
import type { DynamicStyleData } from './core/dynamicStylingMixin';
import { BaseGroup } from './core/BaseGroup';
import { ColorChannelMapFilter } from './core/filters/ColorChannelMapFilter';
import { BlurFilter } from './core/filters/BlurFilter';
import { DuoToneFilter } from './core/filters/DuoToneFilter';
import { GreyscaleFilter } from './core/filters/GreyscaleFilter';
import type { ColorAsset } from './core/asset/ColorAsset';
import type { GradientAsset } from './core/asset/GradientAsset';
import type { NumberArray } from './core/NumberArray';
import type { BaseTimeline } from './libs/BaseTimeline';
import type { LottieAsset } from './core/asset/LottieAsset/LottieAsset';
import type { PlaybackRegion } from './core/PlaybackRegions';
import type { PolygonMask } from './core/mask/PolygonMask';
import { SceneAnnotation } from './video-data/sceneAnnotation';
import { TemplateColorProperties } from './core/template/types';
import type { TextAsset } from './core/asset/TextAsset';
import type { DynamicGraphicAsset } from './core/asset/TextAsset/types';
import type { ColorMap } from './core/asset/ColorMap';
import type { StencilMask } from './core/mask/StencilMask';
import type { Filter } from './core/filters/Filter';
import type { CaptionAsset } from './core/asset/CaptionAsset/asset/CaptionAsset';
import { renderMp4 } from './renderMp4';
import { renderWebm } from './renderWebm';
import { ErrorReporter } from './libs/error-reporter';
import { getVirtualSceneDesigns } from './virtual-designs/parse';
import type { UserOverrides } from './virtual-designs/core';
import { AssetNetworkManager } from './core/AssetNetworkManager';
import type { WatermarkLayout } from './core/watermark/WatermarkLayout';
import { registerDevtoolsFormatters } from './libs/devtools/assetFormatters';
import { LuminaryRendererManager } from './LuminaryRendererManager';

export const STATE_CHANGED_DEBOUNCE_WAIT_TIME = 100;
export const STATE_CHANGED_DEBOUNCE_MAX_WAIT_TIME = 1000;
const DEFAULT_INSTANCE_NAME = 'luminary';

type AddAudioSettings = {
    id?: string;
    data?: Record<string, unknown>;
};

export type LuminarySettings = {
    /**
     * the audio mode
     * @default Config.AUDIO_MODES.INTERACTIVE
     */
    audioMode?: string;
    /**
     * the value of the minimum dimension of the selected aspect ratio
     * @default 400
     */
    resolutionValue?: number;
    /**
     * the media url to load
     * TODO: rename it to mediaSize, refactor values inside Config.*_MEDIA_SIZE
     * @default Config.PREVIEW_MEDIA_SIZE
     */
    mediaSizeToUse?: MediaSize;
    /**
     * draws to canvas
     * @default false
     */
    drawsToCanvas?: boolean;
    /**
     * number of times we try to load a resource before marking it as failed
     * @default 2
     */
    loadStatusMaxCount?: number;
    /**
     * the amount of time (ms) we want to wait for a video to seek
     * @default 10000
     */
    seekWaitTime?: number;
    /**
     * render speed expressed as target frames per second.
     * @default 30
     */
    targetFPS?: number;
    /**
     * multiplier to adjust the ticker's max FPS
     * @default 1
     */
    maxFPSMultiplier?: number;
    /**
     * Specify if font/media/audio assets should be loaded. Can specify them all together (boolean) or individually (map).
     * Is useful for "data luminary" instances where rendering isn't necessary.
     */
    fetchResources?: FetchResources;
    /**
     * @default true
     */
    autoUpdate?: boolean;
    /**
     * @default true
     */
    autoDraw?: boolean;
    /**
     * disables the renderer, if you only want to use this instance for loading and reading video_data
     * @default false
     */
    disableRenderer?: boolean;
    /**
     * Called when the WebGL renderer fails to initialize (e.g. WebGL is unavailable in this browser).
     * Use this to show a fallback UI to the user.
     */
    onRendererInitFailed?: () => void;
    /**
     * disable the creation of display helpers
     * @default false
     */
    enableHelpers?: boolean;
    /**
     * if asset ids from the config should be kept
     * @default false
     */
    preserveAssetIds?: boolean;
    /**
     * debounce our stateChange events
     * @default true
     */
    debounceStateChangedEvents?: boolean;
    /**
     * for metric purposes, identifies when luminary is initialized for encoding/snapshot/recording puporses
     * @default false
     */
    isCloudBrowser?: boolean;
    /**
     * `devicePixelDensity` used by pixi renderer to scale preview for high pixel density devices
     * @default 1
     */
    rendererDevicePixelRatio?: number;

    antialias?: boolean;

    domParent?: HTMLElement;
    /**
     * Useful for debugging to identify different instances of luminary
     */
    name?: string;
    /**
     * Globally disable the creation of generic captions
     */
    disableCaptions?: boolean;

    enableCanvasLottie?: boolean;

    /**
     * Per-instance override for `Luminary.settings.enableEnhancedAssetTransform`. See that
     * field's docs for the behavior it controls.
     */
    enableEnhancedAssetTransform?: boolean;

    maxSimultaneousRequests?: number;
};

export type TimeRange = {
    startTime: number;
    endTime: number;
};

type SceneGroupPlayConfig = {
    playRange: TimeRange;
    sceneGroupRange: TimeRange;
};

/**
 * Removes the PIXI hello message.
 */
PIXI.settings.RENDER_OPTIONS.hello = false;

// Required for Pixi.js devtools extension
window.PIXI = PIXI;

/**
 * Root Application. This object allows user to render and playback video data.
 */
export default class Luminary extends TimeAwareBase {
    /**
     * User's customizable globals for overriding the default Luminary settings.
     */
    static readonly settings = {
        enableCanvasLottie: false,
        /**
         * When true, text assets stop auto-shrinking to fit their bounds and instead
         * clip with an overflow indicator (consumers detect via `TextAsset.isClipping`).
         * Off by default — consumers opt in either by mutating this static before
         * constructing instances, or per-instance via `LuminarySettings.enableEnhancedAssetTransform`.
         */
        enableEnhancedAssetTransform: false,
    };
    /**
     * @internal
     */
    static get _countOfNotDestroyedInstances(): number {
        const context = window || global;
        if (!context._luminary_data) {
            return 0;
        }
        return Object.keys(context._luminary_data).length;
    }

    /**
     * @internal
     */
    static async _destroyAllInstances() {
        const context = window || global;
        if (!context._luminary_data) {
            return;
        }
        for (const key in context._luminary_data) {
            await context._luminary_data[key].destroy();
        }
    }

    /**
     * @internal
     */
    override _classType = 'Luminary';

    // global values
    #audioMode = Config.AUDIO_MODES.INTERACTIVE;
    #muted = false;
    #debounceStateChangedEvents = true;
    #fontManager = getGlobalFontManager();

    /**
     * @internal
     */
    _autoDraw = true;

    #onBufferingPromise: Promise<void> = Promise.resolve();

    // audio
    #audioAssets: Record<string, AudioAsset> = {};
    #audioContext: AudioContext | OfflineAudioContext = null;
    #audioAnimationManager: AudioAnimationManager = null;
    #musicId: string = null;
    #voiceoverId: string = null;
    #sfxVolume: number = Volumes.DEFAULT_SFX_VOLUME;

    // video metadata
    #capabilities = new Capabilities({
        parent: this,
        fetchResources: false,
    });
    #aspectRatio: AspectRatio = AspectRatio.LANDSCAPE;
    #videoHeight = Config.SUPPORTED_VIDEO_HEIGHTS[this.#aspectRatio]['1080p'];
    #renderOutput = Config.RENDER_OUTPUT_SINGLE_VIDEO;
    #paceMultiplier = Config.PACE_MULTIPLIER_DEFAULT;
    #textDirection: TextDirection = TextDirection.LTR;
    #template = new Template({
        parent: this,
        fetchResources: false,
    });
    #sceneDesigns: TemplateBundle['sceneDesigns'] | null = null;
    #preserveAssetIds = false;
    #matchesBrandingCache = new Map();

    #pauseOnPlayComplete: Function = undefined;
    #drawsToCanvas = false;
    #resolutionValue: number;
    // The reference resolution is the one designers and programmers work in.
    // It is currently 1080p
    #referenceResolution: Resolution;

    // The output resolution is the resolution of the output image - this is also the display canvas size.
    #outputResolution: Resolution;
    // Copy of the rendererDevicePixelRatio option. This is handy in other parts of the code, if we want to create
    // textures taking the device pixel ratio into account.
    #rendererDevicePixelRatio = 1;

    /**
     * @internal
     */
    _sceneGroupManager: SceneGroupManager;

    /**
     * Global asset index for O(1) lookups by ID
     * @internal
     */
    #assetsById: Map<string, Asset> = new Map();

    /**
     * Tracks old-to-new ID mappings for backward compatibility with stale references
     * When an asset's ID changes, we keep the old ID pointing to the new ID
     * @internal
     */
    #oldToNewIdMap: Map<string, string> = new Map();

    /**
     * The global timeline
     */
    timeline: TickerTimeline = undefined;

    /**
     * @internal
     */
    _sceneGroupsTimeline: ConcurrentTimeline;

    /**
     * Detect WebGL support.
     */
    static isWebGLSupported = isWebGLSupported(FAST_WEBGL, false);
    /**
     * Detect slow WebGL support.
     */
    static isSlowWebGLSupported = isWebGLSupported(SLOW_WEBGL, false);

    /**
     * Last play request.
     * When multiple play() calls are done in short time, this points to the last call.
     * All previous calls can be ignored.
     */
    #lastPlayRequest: string = null;

    /**
     * @internal
     */
    _sceneGroupPlayConfig: SceneGroupPlayConfig | null = null;

    /**
     * Enables the creation of display helpers
     */
    #enableHelpers = false;

    /**
     * @internal
     */
    _ticker: PIXI.Ticker;
    /**
     * @internal
     */
    _renderSceneQueue = new RenderQueue();
    #rendererManager: LuminaryRendererManager = null;
    #autoStartDrawingToCanvas = false;
    #rendererInitFailed = false;
    #onRendererInitFailed: (() => void) | undefined;

    get renderer(): IRenderer<HTMLCanvasElement> | null {
        this.#ensureRendererInitialized();
        return this.#rendererManager.renderer;
    }

    #ensureRendererInitialized() {
        if (this.destroyed) {
            return;
        }
        if (this.#rendererManager.renderer) {
            return;
        }
        if (this.#rendererInitFailed) {
            return;
        }
        if (this.sceneGroups.length === 0) {
            return;
        }
        try {
            this.#rendererManager.lazyInit(this.#outputResolution, this.#referenceResolution);
        } catch {
            this.#rendererInitFailed = true;
            this.#onRendererInitFailed?.();
            return;
        }
        this.#rendererManager.growIfNeeded(
            this.#outputResolution.width,
            this.#outputResolution.height,
        );
        if (this.#autoStartDrawingToCanvas) {
            this.#autoStartDrawingToCanvas = false;
            void this.startDrawingToCanvas();
        }
    }

    /**
     * @internal
     */
    _workspace = new PIXI.Container();

    settings: LuminarySettings & {
        video?: { seekWaitTime?: number };
        render?: { targetFPS?: number; maxFPSMultiplier?: number };
    };

    #mediaSizeToUse:
        | typeof Config.THUMBNAIL_MEDIA_SIZE
        | typeof Config.PREVIEW_MEDIA_SIZE
        | typeof Config.RENDER_MEDIA_SIZE;

    /**
     * Name used to attach itself to the window
     */
    #debugName: string = null;

    /**
     * @internal
     */
    _reusableTextures = new CanvasReusableTexturesManager(this);

    #enableCanvasLottie = Luminary.settings.enableCanvasLottie;

    #enableEnhancedAssetTransform = Luminary.settings.enableEnhancedAssetTransform;

    /**
     * @internal
     * The canvas for static Renderer to render to
     * This allows sharing the static Renderer between multiple instances of Luminary
     * by providing an individal location for each Luminary instance to render to
     */
    #canvas2d: HTMLCanvasElement;

    constructor(config: LuminarySettings) {
        const settings = {
            fetchResources: true,
            name: DEFAULT_INSTANCE_NAME,
            ...config,
        };
        super(settings);

        this.#attachToWindowForDebug();

        // SYSTEM
        // resolutionValue is the MINIMUM dimension, so height for landscape, width for vertical, and both for square
        this.#resolutionValue = config.hasOwnProperty('resolutionValue')
            ? config.resolutionValue
            : 400;

        if (this.#resolutionValue > 1080) {
            // 1080p rendering (where 1080px is the size of the minimum dimension) is the max
            // resolution we currently support
            throw Error(`1080p is the maximum resolution; ${this.#resolutionValue} is too big.`);
        }

        if (config.hasOwnProperty('audioMode')) {
            this.#audioMode = config.audioMode;
        }
        if (config.hasOwnProperty('enableHelpers')) {
            this.#enableHelpers = config.enableHelpers;
        }
        if (config.hasOwnProperty('isCloudBrowser')) {
            LuminaryActivityProfiler.overrideContext('cloud-browser');
        }
        this.mediaSizeToUse = config.hasOwnProperty('mediaSizeToUse')
            ? config.mediaSizeToUse
            : Config.PREVIEW_MEDIA_SIZE;

        if (config.hasOwnProperty('autoDraw')) {
            this._autoDraw = config.autoDraw;
        }
        if (config.hasOwnProperty('preserveAssetIds')) {
            this.#preserveAssetIds = config.preserveAssetIds;
        }
        if (config.hasOwnProperty('maxSimultaneousRequests')) {
            this._assetNetworkManager.MAX_SIMULTANEOUS_REQUESTS = config.maxSimultaneousRequests;
        }

        // SETTINGS
        this.settings = {};

        // ASSET LOADING SETTINGS
        // We try to optimize the asset loading process to keep Luminary as responsive as possible. This means:
        // 1. Ensuring the asset loads when the connection drops or the server takes to long to respond
        // 2. Loading the best asset given the users connection.
        // Below we can find some parameters which control this behavior.

        // the maximum number of times we run loadStatus per asset. This also helps with the loss of connection as it retries
        // to load the asset once the connection is restored within the pre-defined time frame.
        this.settings.loadStatusMaxCount = config.hasOwnProperty('loadStatusMaxCount')
            ? config.loadStatusMaxCount
            : 2;

        // the amount of time we wait for the next load status check
        this.settings.video = {};

        // the amount of time we want to wait for a video to seek
        this.settings.video.seekWaitTime = config.seekWaitTime ?? 2000;

        // RENDER SETTINGS
        this.settings.render = {
            targetFPS: config.hasOwnProperty('targetFPS') ? config.targetFPS : 30,
            maxFPSMultiplier: config.hasOwnProperty('maxFPSMultiplier')
                ? config.maxFPSMultiplier
                : 1,
        };

        // RENDERER
        this.#referenceResolution = cloneDeep(getReferenceResolution(this.#aspectRatio));
        this.#rendererDevicePixelRatio = Math.ceil(config.rendererDevicePixelRatio ?? 1);
        this.#outputResolution = Luminary.#bakeDevicePixelRatioIntoResolution(
            getResolution(this.#resolutionValue, this.#aspectRatio),
            this.#rendererDevicePixelRatio,
        );

        this.#onRendererInitFailed = config.onRendererInitFailed;
        this.#rendererManager = new LuminaryRendererManager({
            disabled: config.disableRenderer ?? false,
            antialias: config.antialias ?? true,
            onContextLost: this.#trackWebGLContextLostError,
            onContextRestored: this.#trackWebGLContextRestored,
        });
        this.#autoStartDrawingToCanvas = config.drawsToCanvas ?? false;

        this.#canvas2d = (() => {
            const canvas = document.createElement('canvas');
            canvas.width = this.#outputResolution.width;
            canvas.height = this.#outputResolution.height;

            return canvas;
        })();

        // If specified, parents the render canvas to the given dom element so we can see the rendered result.
        if (config.hasOwnProperty('domParent')) {
            config.domParent.appendChild(this.#canvas2d);
        }

        // scene image render queue
        this._renderSceneQueue.name = 'renderSceneQueue';
        this._renderSceneQueue.workFunction = this.#renderSceneImageTask.bind(this);
        this._renderSceneQueue.unlock();

        // FONT
        if (this.shouldFetchFontResources) {
            if (!this.#fontManager.areAllFallbackFontsDoneTryingToLoad) {
                this.#fontManager.once(
                    'loadedAllFallbackFonts',
                    this.#handleAllFallbackFontsLoaded,
                    this,
                );
                void this.#fontManager.loadAllFallbackFonts();
            }
            this.#fontManager.on('loaded', this.#redrawOnFontLoad, this);
        }

        // TIME
        // Below we create our timeline setup which results in the following evaluation steps:
        // 1. tick triggered through requestAnimationFrame
        // 2. update timeline time
        // 3. update and render scene
        // 4. update audio

        // ticker for main playback render loop
        this._ticker = new PIXI.Ticker();
        // Since we need to jump animations to make up lost time (instead of slowing
        // animations based on the ticker rate to make them look smooth regardless)
        // we set the minFPS to zero, so that the deltaMS between ticks is not capped. We need
        // to do it like this because we need to keep up with realtime in order to stay in sync
        // with video tags that get played (since we can't seek videos on each tick)
        this._ticker.minFPS = 0;
        // Don't need to have the ticker run faster than the effective (or scaled) FPS
        this._ticker.maxFPS =
            this.settings.render.targetFPS * this.settings.render.maxFPSMultiplier;

        // timeline
        // allow the ticker to be controlled by our timeline. During this setup we also attach our timeline update
        // method to the ticker's update method. This allows us to update the internal timeline's currentTime on each
        // tick.
        this.timeline = new TickerTimeline(this._ticker, {
            name: 'root_timeline',
        });

        // add a sequential timeline. Our scene's timelines will be parented here and ensure they run in subScene
        this._sceneGroupsTimeline = new ConcurrentTimeline({
            name: 'scene_groups_timeline',
        });
        this.timeline.add(this._sceneGroupsTimeline);

        // call our render method on each tick.
        // IMPORTANT: the render method needs to be added after the ticker timeline is created
        // so that the timeline can update the current time before the render happens
        this._ticker.add(this.#renderTickerUpdate, this);
        this._ticker.stop();

        // audio
        if (this.shouldFetchAudioResources) {
            if (
                this.#audioMode !== Config.AUDIO_MODES.RENDER &&
                typeof AudioContext !== 'undefined'
            ) {
                this.#audioContext = new AudioContext({
                    sampleRate: Config.AUDIO_RENDER_SAMPLE_RATE,
                });
            }
        }
        this.#audioAnimationManager = new AudioAnimationManager(this);

        // EVENTS
        this.#debounceStateChangedEvents = config.debounceStateChangedEvents ?? true;

        // PLAYBACK events
        // event when we completed playing the entire timeline
        const playCompleteListener = () => {
            this.pause();
            this.#emitPlayCompleteAfterNextRender();
        };
        this.timeline.on('end', playCompleteListener);

        let disableCaptions = false;
        if (config.hasOwnProperty('disableCaptions')) {
            disableCaptions = config.disableCaptions;
        }

        if (config.hasOwnProperty('enableCanvasLottie')) {
            this.#enableCanvasLottie = config.enableCanvasLottie;
        }

        if (config.hasOwnProperty('enableEnhancedAssetTransform')) {
            this.#enableEnhancedAssetTransform = config.enableEnhancedAssetTransform;
        }

        // Initialize scene manager
        this._sceneGroupManager = new SceneGroupManager({
            parent: this,
            preserveAssetIds: this.#preserveAssetIds,
            enableHelpers: this.#enableHelpers,
            fetchResources: this.fetchResources,
            autoDraw: this._autoDraw,
            autoUpdate: this.autoUpdate,
            disableCaptions,
            sceneGroupsTimeline: this._sceneGroupsTimeline,
            workspace: this._workspace,
            getAspectRatio: () => this.aspectRatio,
            template: this.template,
            capabilities: this.capabilities,
        });
    }

    /**
     * @internal
     */
    _assetNetworkManager = new AssetNetworkManager();

    get enableCanvasLottie() {
        return this.#enableCanvasLottie;
    }

    get enableEnhancedAssetTransform() {
        return this.#enableEnhancedAssetTransform;
    }

    #attachToWindowForDebug() {
        if (this.#debugName) {
            throw new Error('Cannot attach to window for debug more than once');
        }
        const context = window || global;
        if (!context._luminary_data) {
            context._luminary_data = {};
        }

        // find a unique name using a numeric suffix
        let name = this.name;
        let i = 1;
        while (context._luminary_data[name]) {
            name = `${this.name}_${i++}`;
        }
        this.#debugName = name;
        context._luminary_data[name] = this;
    }

    #detachFromWindowForDebug() {
        const context = window || global;
        if (context._luminary_data[this.#debugName] !== this) {
            throw new Error(
                `Unexpected: _luminary_data does not match this instance for ${this.#debugName}`,
            );
        }
        delete context._luminary_data[this.#debugName];
        this.#debugName = null;
    }

    #trackWebGLContextLostError = () => {
        const { rendererCreatedAt, webGLContextLostAt } = this.#rendererManager;
        const timeSinceRendererCreatedMs =
            rendererCreatedAt !== null && webGLContextLostAt !== null
                ? webGLContextLostAt - rendererCreatedAt
                : null;
        console.warn('WebGL context lost');
        if (!window.analytics) {
            return;
        }
        window.analytics.track('Error: webglcontextlost', {
            luminaryName: this.name,
            pixiCachedTexturesNumber: ResourcesStats.getPixiCachedTexturesNumber(),
            pixiCachedTexturesAllocatedMemory:
                ResourcesStats.getPixiCachedTexturesAllocatedMemory(),
            reusedTexturesAttachedTexturesNumber:
                this._reusableTextures.getDebugInfo().attachedTextures.size,
            reusedTexturesDetachedTexturesNumber:
                this._reusableTextures.getDebugInfo().detachedTextures.length,
            webglRenderer: this.#rendererManager.webglContextInfo.renderer,
            webglVendor: this.#rendererManager.webglContextInfo.vendor,
            webglExtensions: this.#rendererManager.webglContextInfo.extensions,
            isSlowWebGLSupported: Luminary.isSlowWebGLSupported,
            isWebGLSupported: Luminary.isWebGLSupported,
            devicePixelRatio: window.devicePixelRatio,
            outputResolutionHeight: this.outputResolution.height,
            outputResolutionWidth: this.outputResolution.width,
            templateId: this.template.id,
            timeSinceRendererCreatedMs,
        });
    };

    #trackWebGLContextRestored = () => {
        const timeSinceContextLostMs =
            this.#rendererManager.webGLContextLostAt !== null
                ? performance.now() - this.#rendererManager.webGLContextLostAt
                : null;
        console.log('WebGL context restored');
        if (!window.analytics) {
            return;
        }
        window.analytics.track('Info: webglcontextrestored', {
            luminaryName: this.name,
            timeSinceContextLostMs,
        });
    };

    /**
     * Returns True if we automatically process any components changes
     */
    override get autoUpdate(): boolean {
        return super.autoUpdate;
    }

    override set autoUpdate(value) {
        if (this.autoUpdate === value) {
            return;
        }
        assertBoolean(value);
        Object.values(this.#audioAssets).forEach((a) => {
            a.autoUpdate = value;
        });
        this._sceneGroupManager.autoUpdate = value;
        this.#template.autoUpdate = value;
        super.autoUpdate = value;
    }

    /**
     * Returns True if we automatically redraw any PIXI components changes
     */
    get autoDraw(): boolean {
        return this._autoDraw;
    }

    /**
     * Determine whether or not we want to redraw all changes, all the time. If we want to visualize all changes one
     * by one while editing, then we would set this to True. If on the other hand we're loading a big batch of changes
     * while loading a video for instance, then we would set this to False.
     */
    set autoDraw(value: boolean) {
        if (this._autoDraw === value) {
            return;
        }
        assertBoolean(value);
        this._autoDraw = value;
        this._sceneGroupManager.autoDraw = value;
    }

    /**
     * Returns whether or not the object has changes that need to be drawn.
     */
    get requiresRedraw(): boolean {
        if (
            this.shouldFetchFontResources &&
            !this.#fontManager.areAllFallbackFontsDoneTryingToLoad
        ) {
            return true;
        }
        return this._sceneGroupManager.requiresRedraw;
    }

    get shouldFetchFontResources() {
        return (
            this.fetchResources === true ||
            (typeof this.fetchResources === 'object' && this.fetchResources.fonts === true)
        );
    }

    get shouldFetchAudioResources() {
        return (
            this.fetchResources === true ||
            (typeof this.fetchResources === 'object' && this.fetchResources.audio === true)
        );
    }

    #handleAllFallbackFontsLoaded() {
        this.#redrawOnFontLoad();
        this._emitChanged();
    }

    #redrawOnFontLoad() {
        // Note we can only do this shortcut with canvas text enabled, the old text asset
        // requires this event with the sourceUrl passed through to emit can play
        if (!this.#fontManager.areAllFallbackFontsDoneTryingToLoad) {
            // If fallback fonts haven't loaded yet, we can skip updating text
            // since we'll still get another emitted event once they all finish loading
            return;
        }
        // Pass the event down to each text asset
        this.batchRun(() => {
            this.getAssets().forEach((asset) => {
                if (Config.isTextAsset(asset) || Config.isFancyCaptionAsset(asset)) {
                    asset._redrawOnFontLoad();
                }
            });

            this.sceneGroups.forEach((sceneGroup) => {
                sceneGroup.captions.asset._redrawOnFontLoad();
            });
        });
    }

    /**
     * Synchronously load data, following our video-data schema, as Luminary state. Note that this method should only be
     * used on a freshly constructed Luminary instance as we currently don't clear the state beforehand.
     */
    override load(gdata: RawVideoData | OpaqueVideoData) {
        assertObject(gdata);
        if (this.sceneGroups.length !== 0) {
            throw Error('Unable to load data, luminary.sceneGroups.length is not zero.');
        }
        const originalTemplate = this.template.id;

        // start performance timing
        const activityId = uuid();
        LuminaryActivityProfiler.start(activityId);

        // migrate video data to latest version
        const migratedData = Migrator.updateVideoData(gdata);

        this.batchRun(() => {
            this.timeline.autoUpdate = false;
            super.load(migratedData);
            // we have to load the template before our slides since they contain template referenced attributes
            this._sceneGroupManager.load(migratedData);

            // We need to update timelines to ensure the duration, stop time, etc are correct.
            // reset evaluation properties -- if set to true, it's at this point where we actually evaluate the entire video.
            // This involves, creating all the scene's children and drawing them. If in slideImageRender mode, it's also at
            // this point that we'll start creating and rendering scene image renders.
            this.timeline.autoUpdate = true;
        });

        const metricLabels = METRIC_LOAD.getLabels(migratedData.slides.length);
        LuminaryActivityProfiler.end(activityId, METRIC_LOAD.name, metricLabels);

        if (this.template.id !== originalTemplate) {
            // in a scenario where we are re-using a luminary instance to load a new video,
            // luminary.load() will set this.template.id to the new video's template id.
            // if we were previously passed a templateBundle, then the scene designs list will
            // be from the old template. Clear that, so that we do not get into a state where there is a
            // conflict between the template and the scene designs
            this.#sceneDesigns = null;
        }

        return this;
    }

    /**
     * Validates the state of the Luminary object. Since we can create an invalid state we need to validate it
     * before saving or playing it back.
     */
    validateState(): ValidationResult {
        const validations = [validateUniqueMediaAssetIds, validateAspectRatioConsistency];
        for (const validation of validations) {
            const { passed, message } = validation(this);
            if (!passed) {
                return { passed, message };
            }
        }

        return { passed: true, message: 'State is valid' };
    }

    /**
     * Saves out Luminary's state following our video-data schema. The result of save should
     * not be parsed or otherwise modified outside of Luminary.
     */
    override save(): OpaqueVideoData {
        if (!this.autoUpdate) {
            this.autoUpdate = true;
            this.autoUpdate = false;
        }
        const { passed, message } = this.validateState();
        if (!passed) {
            throw Error(`Unable to save state, validation failed: ${message}`);
        }
        if (this.sceneGroups.some((sceneGroup) => sceneGroup.scenes.length === 0)) {
            ErrorReporter.capture('Video contains scene groups with no scenes');
        }
        const activityId = uuid();
        const metricLabels = METRIC_SAVE.getLabels(this.scenes.length);
        LuminaryActivityProfiler.start(activityId);
        const data = {
            ...super.save(),
            ...this._sceneGroupManager.save(),
        };

        LuminaryActivityProfiler.end(activityId, METRIC_SAVE.name, metricLabels);
        return data;
    }

    get musicAsset(): AudioAsset {
        if (this.#musicId) {
            return this.#audioAssets[this.#musicId];
        }
    }

    get voiceoverAsset(): AudioAsset {
        if (this.#voiceoverId) {
            return this.#audioAssets[this.#voiceoverId];
        }
    }

    /**
     * @internal
     */
    _audioAssetsCustomLoad(audioData) {
        Object.keys(audioData).forEach((audioId) => {
            this.addAudio({ data: audioData[audioId] });
        });
    }

    /**
     * @internal
     */
    _audioAssetsCustomSave() {
        const retVal = {};
        Object.values(this.audioAssets).forEach((audioAsset) => {
            retVal[audioAsset.id] = audioAsset.save();
        });
        return retVal;
    }

    override get root() {
        return this;
    }

    set mediaSizeToUse(value) {
        assertInEnum(value, Config.ALL_MEDIA_SIZES);
        this.#mediaSizeToUse = value;
    }

    get mediaSizeToUse() {
        return this.#mediaSizeToUse;
    }

    /**
     * Start continuously rendering to the canvas.
     */
    async startDrawingToCanvas() {
        if (!this.renderer) {
            console.warn(
                'unable to start drawing to canvas because renderer not initialized - skipping',
            );
            return;
        }
        this.#drawsToCanvas = true;
        await this._renderSceneQueue.lock();
        this.renderer.clear();
        this._ticker.start();
    }

    /**
     * Stop continuously rendering to the canvas
     */
    stopDrawingToCanvas() {
        if (!this.renderer) {
            return;
        }
        this._ticker.stop();
        this.#drawsToCanvas = false;

        // we're about to unlock the render queue, ensure no process is running which requires us to draw
        // directly to the canvas
        if (this.isPlaying()) {
            this.stop();
        }
        this._renderSceneQueue.unlock();
    }

    /**
     * Returns whether or not we're continuously rendering to the canvas
     */
    get isDrawingToCanvas(): boolean {
        return this.#drawsToCanvas;
    }

    /**
     * The audio mode which defines how audio operates for the luminary instance. Cannot be changed
     * after initialization. Refer to [AUDIO_MODES]{@link Config.Config.AUDIO_MODES}.
     */
    get audioMode(): string {
        return this.#audioMode;
    }

    /**
     * Indicates if luminary is still processing the render queue. Note that luminary has to also
     * be finished loading all assets in order for this to be true.
     */
    isRenderQueueDoneProcessing() {
        return (
            this._renderSceneQueue.isEmpty() &&
            !this._renderSceneQueue.taskInProgress() &&
            this.areSceneGroupAssetsDoneTryingToLoad
        );
    }

    /**
     * Returns true if all scene group level assets (deeply) within this object are done trying to load
     */
    get areSceneGroupAssetsDoneTryingToLoad(): boolean {
        return this.sceneGroups.every((s) => s.areAssetsDoneTryingToLoad);
    }

    protected override _getLifecycleAwareObjects(): LifecycleAware[] {
        const objects: LifecycleAware[] = [
            ...Object.values(this.#audioAssets),
            ...this.sceneGroups,
        ];
        // The font manager is a bit different because it's shared globally, so we can't pass the fetch
        // resource settings to it from each luminary instance.
        // Instead, each luminary instance needs to manage if fonts should be part of lifecycle events
        if (this.shouldFetchFontResources) {
            objects.push(this.#fontManager);
        }
        return objects;
    }

    /**
     * @internal
     */
    _getSceneIndex(sceneId: string): number {
        let result = -1;

        for (let i = 0; i < this.scenes.length; i++) {
            if (this.scenes[i].id === sceneId) {
                result = i;
                break;
            }
        }

        return result;
    }

    /**
     * The position of the head of the video. A value of zero sets us at the start of the video, while
     * `luminary.currentTime = luminary.duration` places us at the end of the video.
     */
    get currentTime(): number {
        return this.timeline.currentTime;
    }

    set currentTime(value) {
        assertNumber(value);
        assertMinimum(value, 0);
        assertMaximum(value, this.duration);
        this.timeline.currentTime = value;
    }

    /**
     * This method returns the position of the head of the video as progress. A value of 0 means we're at the start of
     * the video. While a value of 1 means that we're at the end of the video.
     */
    get progress(): number {
        return this.timeline.progress;
    }

    /**
     * Return true if all the video's resources have been loaded successfully, and false otherwise.
     * Used to determine if we can start playback.
     * To subscribe to changes, see the onCanPlay event.
     */
    override get canPlay(): boolean {
        if (this.sceneGroups.length === 0) {
            return false;
        }
        return super.canPlay;
    }

    /**
     * @internal
     */
    _emitAssetsCanPlay() {
        /**
         * The way canPlay works means that we should only be emitting this event when making a change that affects
         * playback status (updating MediaAsset.sourceData, TextAsset.fontUrl) and we should only emit the event ONCE.
         * This is because each asset emits `onCanPlay` a single time and we only emit here at the root level when ALL
         * children are reporting `canPlay == true`. This in essence debounces the event so we should only be emitting
         * it once.
         */
        // TODO rename? Either canPlay or assetsCanPlay
        this.emit('onCanPlay');
    }

    /**
     * Determines if the workspace playing back
     */
    isPlaying(): boolean {
        if (!this.timeline) {
            return false;
        }
        return this.timeline.playing();
    }

    async onCanPlay() {
        if (this.canPlay) {
            return;
        }
        return new Promise<void>((resolve) => {
            this.once('onCanPlay', () => {
                resolve();
            });
        });
    }

    /**
     * Resolves on the next stateChanged event
     */
    async onStateChanged() {
        return new Promise<void>((resolve) => {
            this.once('stateChanged', () => {
                resolve();
            });
        });
    }

    async onPlayComplete() {
        if (!this.isPlaying() && this.#lastPlayRequest === null) {
            return;
        }
        return new Promise<void>((resolve) => {
            this.once('playComplete', () => {
                resolve();
            });
        });
    }

    /**
     * Resolves once all slides images have been rendered. This also means that all assets have
     * finished loading and the internal state throughout luminary is up to date.
     */
    async onAllSlideImagesRendered() {
        await this.#onSceneGroupsAssetsLoaded();

        if (this.isRenderQueueDoneProcessing()) {
            return;
        }
        await new Promise<void>((resolve) => {
            const checkRenderedAll = () => {
                if (this.isRenderQueueDoneProcessing()) {
                    resolve();
                    this.off('onRenderedSlideImage', checkRenderedAll);
                }
            };
            this.on('onRenderedSlideImage', checkRenderedAll);
        });
    }

    /**
     * Resolves once all sceneGroup level assets have loaded. Will throw an error if any assets have failed to load.
     */
    async #onSceneGroupsAssetsLoaded(): Promise<void> {
        if (this.areSceneGroupAssetsDoneTryingToLoad) {
            return;
        }
        await new Promise<void>((resolve) => {
            const checkLoaded = () => {
                if (this.areSceneGroupAssetsDoneTryingToLoad) {
                    this.off('stateChanged', checkLoaded);
                    resolve();
                }
            };
            this.on('stateChanged', checkLoaded);
        });
    }

    /**
     * Resolves once all assets have loaded. Will throw an error if any assets have failed to load.
     */
    async onAssetsLoaded(): Promise<void> {
        if (this.isLoaded) {
            return;
        }
        await new Promise<void>((resolve, reject) => {
            const checkLoaded = () => {
                if (this.isLoaded) {
                    this.off('stateChanged', checkLoaded);
                    resolve();
                } else if (this.areAssetsDoneTryingToLoad) {
                    this.off('stateChanged', checkLoaded);
                    reject(new Error('assets failed to load'));
                }
            };
            this.on('stateChanged', checkLoaded);
        });
    }

    /**
     * Resolves once all assets have either successfully loaded or failed to load.
     */
    async onAssetsDoneTryingToLoad(): Promise<void> {
        if (this.areAssetsDoneTryingToLoad) {
            return;
        }
        await new Promise<void>((resolve) => {
            const checkLoaded = () => {
                if (this.areAssetsDoneTryingToLoad) {
                    this.off('stateChanged', checkLoaded);
                    resolve();
                }
            };
            this.on('stateChanged', checkLoaded);
        });
    }

    /**
     * Start playback. To play a specific time range, provide start and end times.
     * If the start time is omitted, playback starts at Luminary.currentTime.
     * If end time is omitted, playback ends at Luminary.duration.
     * Playback will render the entire workspace (including scene transitions).
     *
     * @example
     * ```js
     *  // Play full video from current time.
     *  luminary.play();
     *
     *  // Play a specific time range (in milliseconds)
     *  luminary.play({ startTime: 300, endTime: 4500 });
     */
    async play(range?: Partial<TimeRange>): Promise<void> {
        await this._play(range);
    }

    /**
     * @internal
     * @param range - Optional range to play. Will be clamped to the scene group range if a scene group is passed.
     * @param sceneGroup - Optional scene group to play. If passed without `range`, the scene group plays from the start to the end.
     */
    async _play(range?: Partial<TimeRange>, sceneGroup?: SceneGroup): Promise<void> {
        const { passed, message } = this.validateState();
        if (!passed) {
            throw Error(`Unable to play state, validation failed: ${message}`);
        }

        const { startTime, endTime } = this.#inferValidPlayRange(range, sceneGroup);
        this.#resumeAudioContext();

        if (!this.isDrawingToCanvas) {
            console.warn(`Unable to play since we're not drawing to the canvas`);
        }

        // Play request manager.
        const requestId = uuid();
        this.#lastPlayRequest = requestId;

        await this.#seek(startTime, BufferingMode.PrepareForPlayback);

        // In case of multiple play() calls in a very short period, ex. swap scene tab
        // where playback starts on mouseenter and stops on mouseleave, every play request
        // is trying to seek the timeline to a new start time. However, we only need to
        // play last completed request. Rest of the requests can be ignored.
        if (this.#lastPlayRequest !== requestId) {
            return;
        }

        // If we are playing in a scene group, we want to disable transitions at the start and at the end so that
        // we see only the transitions within a scene group. We use this range to filter for only scenes in the group
        // thereby skipping transitions at the group start and end.
        this._sceneGroupPlayConfig = null;
        if (sceneGroup) {
            this._sceneGroupPlayConfig = {
                playRange: {
                    startTime,
                    endTime,
                },
                sceneGroupRange: {
                    startTime: sceneGroup.startTime,
                    endTime: sceneGroup.endTime,
                },
            };
        }

        // register our pause method to ensure we can remove it from listening to the playComplete event
        // in case it doesn't get emitted during the lifecycle of this play. This can happen when the
        // user pauses playback and then calls another play. In that case the playComplete event would
        // have never been sent but the listener would remain, causing it the trigger at an unexpected
        // time.
        this.#deleteOnPlayCompleteCallbackWhenPresent();
        this.#createOnPlayCompleteCallback(endTime);

        this.timeline.play();
        this._propagatePlay(endTime);

        // once we rendered the first frame
        await new Promise<void>((resolve) => {
            this.once('onRenderComplete', () => {
                // This event is not emitted immediately after seek has completed.
                // It is possible a new play request was added before this event is emitted,
                // ex. in case the user moved to a different scene preview BEFORE render,
                // but AFTER seek completed. Such requests need to be ignored.
                if (this.#lastPlayRequest !== requestId) {
                    resolve();
                    return;
                }
                resolve();
                this.emit('playStart', startTime, endTime);
            });
        });
    }

    /**
     * Infer the play range. The rules are quite specific, this method implements them in clear sections.
     */
    #inferValidPlayRange(playRange?: Partial<TimeRange>, sceneGroup?: SceneGroup): TimeRange {
        const playRangeSanityCheck = (playRange: TimeRange) => {
            if (playRange.startTime) {
                assertMinimum(playRange.startTime, 0);
                assertMaximum(playRange.startTime, this.duration - 1);
            }
            if (playRange.endTime) {
                assertMinimum(playRange.endTime, 1);
                assertMaximum(playRange.endTime, this.duration);
            }

            if (playRange.startTime > playRange.endTime - 1) {
                ErrorReporter.setContext('play', { playRange, hasSceneGroup: !!sceneGroup });
                throw new Error('Play range is invalid');
            }
        };

        // Break down the calculations into simple steps, it gets out of hand otherwise.

        // This is typical of playing after pausing. Resume from where we left off.
        if (!playRange && !sceneGroup) {
            return { startTime: this.currentTime, endTime: this.duration };
        }

        if (playRange && !sceneGroup) {
            const startTime = playRange.startTime ?? this.currentTime;
            const endTime = playRange.endTime ?? this.duration;
            const result = { startTime, endTime };
            playRangeSanityCheck(result);
            return result;
        }

        // Just play the entire scene group.
        if (!playRange && sceneGroup) {
            return {
                startTime: sceneGroup.startTime,
                endTime: sceneGroup.endTime,
            };
        }

        // We have both a play range and a scene group.
        // Clamp the playRange to the scene group.
        const startTime = playRange.startTime ?? sceneGroup.startTime;
        const endTime = playRange.endTime ?? sceneGroup.endTime;
        const result = { startTime, endTime };
        playRangeSanityCheck(result);

        return result;
    }

    /**
     * @internal
     */
    _propagatePlay(endTime?: number) {
        Object.values(this.#audioAssets).forEach((a) => a._propagatePlay());
        this.#scheduleAllSfxPlayback(this.currentTime, endTime);

        // since a video's playback gets triggered by the slide start event we have to
        // start it manually when we're not at the start of a slide
        this.getActiveSceneGroupsAt(this.currentTime).forEach((sceneGroup) => {
            sceneGroup._propagatePlay();
        });
    }

    /**
     * Schedule all SFX playback at the root level.
     * This allows SFX to be anchored at certain events (like the start of a scene),
     * but still have a negative offset.
     * @param referenceTime - The reference time (ms) to calculate startDelay from
     * @param endTime - Optional end time (ms) to filter out SFX that would play after this time
     */
    #scheduleAllSfxPlayback(referenceTime: number, endTime?: number): void {
        this.getAllSfx().forEach(({ owner, sfx, playbackTime }) => {
            if (endTime !== undefined && playbackTime >= endTime) {
                return;
            }
            const audioAsset = owner._getSfxAudioAsset(sfx.id);
            if (audioAsset) {
                audioAsset.startDelay = playbackTime - referenceTime;
                audioAsset._propagatePlay();
            }
        });
    }

    /**
     * Buffer all SFX at the root level.
     * @param bufferingMode - The buffering mode to use
     */
    async #bufferAllSfx(bufferingMode: BufferingMode): Promise<void> {
        await Promise.all(
            this.getAllSfx().map(async ({ owner, sfx }) => {
                const audioAsset = owner._getSfxAudioAsset(sfx.id);
                if (!audioAsset) return;
                // Always buffer to the start of the SFX,
                // the startDelay determines when it should play
                await audioAsset._bufferAtTime(0, bufferingMode);
            }),
        );
    }

    /**
     * @internal
     */
    _getMatchingSceneForTime(time: number) {
        const startEndMatch = (timeSlice: [number, number]) =>
            timeSlice[0] === time || timeSlice[1] === time;
        const matchingSceneIndex = this.getTimeSlices().findIndex(startEndMatch);
        if (matchingSceneIndex !== -1) {
            return this.scenes[matchingSceneIndex];
        }
    }

    #deleteOnPlayCompleteCallbackWhenPresent() {
        if (typeof this.#pauseOnPlayComplete === 'function') {
            this.timeline.removeCallback(this.#pauseOnPlayComplete);
            this.#pauseOnPlayComplete = null;
        }
    }

    #createOnPlayCompleteCallback(end: number) {
        this.#pauseOnPlayComplete = () => {
            this.pause();
            // timeline end already emits, so only do it if end isn't reached
            if (end < this.duration) {
                this.#emitPlayCompleteAfterNextRender();
            }
        };
        this.timeline.addCallback(this.#pauseOnPlayComplete, end);
    }

    #emitPlayCompleteAfterNextRender() {
        this.once('onRenderComplete', () => {
            this._sceneGroupPlayConfig = null;
            this.#lastPlayRequest = null;
            this.emit('playComplete');
        });
    }

    /**
     * Pause playback
     */
    pause() {
        if (this.destroyed) {
            return;
        }
        if (!this.isDrawingToCanvas) {
            console.warn(`Unable to pause since we're not drawing to the canvas`);
        }
        if (this.timeline) {
            this.timeline.pause();
        }
        this._propagatePause();
        this.#lastPlayRequest = null;
        this.emit('pause');
    }

    /**
     * Exit playback
     */
    stop() {
        if (this.destroyed) {
            return;
        }

        this.pause();

        if (this.timeline) {
            this.timeline.reset();
        }

        // preemptively seek luminary back to start, so next play request can start sooner
        void this.#seek(0, BufferingMode.PrepareForPlayback);

        this._sceneGroupPlayConfig = null;

        this.emit('onStop');
    }

    get canvas(): HTMLCanvasElement | null {
        if (this.#rendererManager.disabled) {
            return null;
        }
        return this.#canvas2d;
    }

    get canvasParent(): ParentNode | null {
        if (this.#rendererManager.disabled) {
            return null;
        }
        return this.#canvas2d.parentNode ?? null;
    }

    set canvasParent(domElement: ParentNode | null) {
        if (this.#rendererManager.disabled) {
            return;
        }

        if (domElement) {
            if (this.#canvas2d.parentNode !== domElement) {
                domElement.appendChild(this.#canvas2d);
            }
            return;
        }

        if (this.#canvas2d.parentNode) {
            this.#canvas2d.parentNode.removeChild(this.#canvas2d);
        }
    }

    // expose getting #capabilities but not setting it!
    get capabilities() {
        return this.#capabilities;
    }

    get aspectRatio(): AspectRatio {
        return this.#aspectRatio;
    }

    /**
     * The Aspect Ratio represents the shape of the resolution. It's the proportional relationship between our output's
     * width and height. We currently use the following aspect ratios:
     * - square = 1:1
     * - landscape = 16:9
     * - vertical = 9:16
     */
    set aspectRatio(value: AspectRatio) {
        if (this.#aspectRatio === value) {
            return;
        }
        // Since branding scenes are built from a partial, we can change it's aspect ratio without
        // the template bundle.
        const hasOnlyBrandingScene = this.scenes.every(
            (scene) => scene.sceneCategory === Config.CATEGORY_BRAND,
        );
        if (!this.isTemplateBundleSet && this.sceneGroups.length > 0 && !hasOnlyBrandingScene) {
            // If there are sceneGroups to migrate, we need the new scene design configs in order
            // to update the aspect ratio.
            // Note that its fine to set the aspect ratio when there are no scenes yet.
            throw new Error('Cannot switch aspect ratio without the template bundle loaded.');
        }
        assertInArray(value, ALL_ASPECT_RATIOS);
        this._reusableTextures.destroy();
        this.#aspectRatio = value;
        this.requiresUpdate = true;
    }

    get resolutionValue() {
        return this.#resolutionValue;
    }

    /**
     * This is the reference frame we (programmers and designers) think in. All scene designs are created in the
     * `referenceResolution`. It is currently 1080p.
     *  At runtime, all Luminary objects will have coordinates in this reference frame when accessing them
     *  e.g. in the debugger. There is no need to think in the `outputResolution` frame, unless code is dealing
     *  with render textures, which should be sized appropriately for the `outputResolution`.
     */
    get referenceResolution(): Resolution {
        return Object.assign({}, this.#referenceResolution);
    }

    /**
     * The output resolution is the size Luminary renders a frame into. E.g. when rendering to a canvas, this is the
     * size of the canvas.
     */
    get outputResolution(): Resolution {
        return Object.assign({}, this.#outputResolution);
    }

    /**
     *  This is the screen's pixel density.
     *
     * This value is already baked into the `outputResolution`, e.g. if Luminary was constructed with a
     * `resolutionValue` of 240, the `outputResolution` witll be 320x240 if `rendererDevicePixelRatio` is 1, or 640x480
     * if `rendererDevicePixelRatio` is 2.
     */
    get rendererDevicePixelRatio(): number {
        return this.#rendererDevicePixelRatio;
    }

    static #bakeDevicePixelRatioIntoResolution(resolution: Resolution, devicePixelRatio: number) {
        return {
            width: resolution.width * devicePixelRatio,
            height: resolution.height * devicePixelRatio,
        };
    }

    #checkAndFixResolution() {
        const resolution = getResolution(this.#resolutionValue, this.aspectRatio);

        if (isEqual(this.referenceResolution, resolution)) {
            return;
        }

        this.#referenceResolution = cloneDeep(getReferenceResolution(this.aspectRatio));
        this.#outputResolution = Luminary.#bakeDevicePixelRatioIntoResolution(
            resolution,
            this.rendererDevicePixelRatio,
        );
    }

    #updateSceneGroupManager() {
        this._sceneGroupManager._update();
    }

    /**
     * @internal
     */
    _updateAudioAnimationManager() {
        this.#audioAnimationManager.update();
    }

    #updateAspectRatio() {
        this._sceneGroupManager._updateAspectRatio(this.aspectRatio);
    }

    #updateCanvasSize() {
        const outputW = this.#outputResolution.width;
        const outputH = this.#outputResolution.height;

        this.#ensureRendererInitialized();
        this.#rendererManager.growIfNeeded(outputW, outputH);

        if (this.#canvas2d.width === outputW && this.#canvas2d.height === outputH) {
            return;
        }

        this.#canvas2d.width = outputW;
        this.#canvas2d.height = outputH;
    }

    #updateAssetColorGradingProperties() {
        this.getAssets().forEach((asset) => {
            // If the asset is a media asset, ensure it has the updated color grading filter settings
            if (asset instanceof MediaAsset) {
                asset.colorGradingManager.updateFilterProperties();
            }
        });
    }

    /**
     * Browsers don't allow AudioContexts to start before the user has interacted with the page
     * Here, we have a simple method to check if the AudioContext is paused, and call resume on it
     * if it has.
     */
    #resumeAudioContext() {
        if (
            this.#audioContext &&
            this.#audioContext.state === 'suspended' &&
            !(this.#audioContext instanceof OfflineAudioContext)
        ) {
            void this.#audioContext.resume();
        }
    }

    /**
     * Sets defaults for any missing fields on the audio object.
     */
    #setAudioDefaults(audio: AudioSourceData) {
        if (audio === null) {
            return;
        }
        if (!audio.hasOwnProperty('license')) {
            audio.license = Config.CC0_LICENSE;
        }
        if (!audio.hasOwnProperty('attribution')) {
            audio.attribution = null;
        }
        if (!audio.hasOwnProperty('description')) {
            audio.description = null;
        }
        if (!audio.hasOwnProperty('tags')) {
            audio.tags = [];
        }
        if (!audio.hasOwnProperty('title')) {
            audio.title = null;
        }
        if (!audio.hasOwnProperty('name')) {
            audio.name = '';
        }
        if (!audio.hasOwnProperty('quality')) {
            audio.quality = 0;
        }
    }

    set musicId(id) {
        if (this.#musicId === id) {
            return;
        }

        if (id) {
            assertKeyInObject(id, this.#audioAssets);
        }

        this.#musicId = id;
    }

    get musicId() {
        return this.#musicId;
    }

    /**
     * The global background music
     */
    get music(): AudioSourceData | null {
        if (this.#musicId) {
            return this.musicAsset.sourceData;
        }
        return null;
    }

    set music(music: AudioSourceData | string | null) {
        if (isEqual(this.music, music)) {
            return;
        }

        this.batchRun(() => {
            if (!music) {
                // if there is music, delete it
                if (this.#musicId) {
                    this.destroyAudio(this.musicAsset);
                    this.#musicId = null;
                }
            } else if (typeof music === 'string') {
                // we are setting the audio to a uuid, which should already
                // be in the audioAssets object
                assertKeyInObject(music, this.#audioAssets);
                this.#musicId = music;
            } else {
                // we are setting the audio field by passing in audio sourceData
                assertIsValidAudio(music, true);

                // since audio is an object, we maintain our own 'immutable' copy of it
                const copiedAudio = cloneDeep(music);
                this.#setAudioDefaults(copiedAudio);
                Object.freeze(copiedAudio);

                // check if we already have this audio in the audioAssets
                let musicId = null;
                Object.values(this.#audioAssets).forEach((audioAsset) => {
                    if (isEqual(audioAsset.source, music)) {
                        musicId = audioAsset.id;
                    }
                });

                if (!musicId) {
                    if (this.#musicId) {
                        this.destroyAudio(this.musicAsset);
                        this.#musicId = null;
                    }

                    // and add a new one
                    const audioAsset = this.addAudio({});
                    audioAsset.sourceData = copiedAudio;
                    musicId = audioAsset.id;

                    // background music by default should loop
                    audioAsset.loop = true;
                }
                this.#musicId = musicId;
            }

            this.requiresUpdate = true;
        });
    }

    set voiceoverId(id) {
        if (this.#voiceoverId === id) {
            return;
        }

        if (id) {
            assertKeyInObject(id, this.#audioAssets);
        }

        this.#voiceoverId = id;
    }

    get voiceoverId() {
        return this.#voiceoverId;
    }

    /**
     * The voiceover is a second global audio track specifically for a spoken voiceover track.
     * This provides first class support in luminary for voiceovers, allowing for the volume
     * and ducking to perform differently than the audio track, and without requiring the
     * development of a flexible multi track audio solution.
     */
    get voiceover(): string | AudioSourceData {
        if (this.#voiceoverId) {
            // luminary.voiceover only supports sourceData and string, not html media element
            return this.#audioAssets[this.#voiceoverId].source as string | AudioSourceData;
        }
        return null;
    }

    set voiceover(voiceover: AudioSourceData | string | null) {
        if (isEqual(this.voiceover, voiceover)) {
            return;
        }

        if (!voiceover) {
            // if there is a voiceover, delete it
            if (this.#voiceoverId) {
                this.destroyAudio(this.#audioAssets[this.#voiceoverId]);
                this.#voiceoverId = null;
            }
        } else if (typeof voiceover === 'string') {
            // we are setting the voiceover to a uuid, which should already
            // be in the audioAsset object
            assertKeyInObject(voiceover, this.#audioAssets);
            this.#voiceoverId = voiceover;
        } else {
            // we are setting the voiceover field by passing in audio sourceData
            assertIsValidAudio(voiceover, true);

            // since voiceover is an object, we maintain our own 'immutable' copy of it
            const copiedVoiceover = cloneDeep(voiceover);
            this.#setAudioDefaults(copiedVoiceover);
            Object.freeze(copiedVoiceover);

            // check if we already have this audio in the audioAssets
            let audioId = null;
            Object.values(this.#audioAssets).forEach((audioAsset) => {
                if (isEqual(audioAsset.source, voiceover)) {
                    audioId = audioAsset.id;
                }
            });

            if (!audioId) {
                // remove the old voiceover
                if (this.#voiceoverId) {
                    this.destroyAudio(this.#audioAssets[this.#voiceoverId]);
                    this.#voiceoverId = null;
                }

                // and add a new one
                const audioAsset = this.addAudio({});
                audioAsset.sourceData = copiedVoiceover;
                audioId = audioAsset.id;

                // voiceover audio be default should not loop
                audioAsset.loop = false;
            }
            this.#voiceoverId = audioId;
        }

        this.requiresUpdate = true;
    }

    /**
     * Global volume for all sound effects (0-100).
     * This controls the volume of SFX attached to scenes and assets.
     */
    get sfxVolume(): number {
        return this.#sfxVolume;
    }

    set sfxVolume(volume: number) {
        assertNumberBetween(volume, 0, 100);
        if (this.#sfxVolume === volume) {
            return;
        }
        this.#sfxVolume = volume;
        this.requiresUpdate = true;
    }

    /**
     * Returns all SFX from all scenes sorted by playback time.
     */
    getAllSfx(): SfxResult[] {
        const results: SfxResult[] = [];

        this.sceneGroups.forEach((sceneGroup) => {
            sceneGroup.getAssets().forEach((asset) => {
                asset.sfx.forEach((sfx) => {
                    results.push({
                        owner: asset,
                        sfx,
                        playbackTime: asset._getSfxPlaybackTime(sfx),
                    });
                });
            });

            sceneGroup.scenes.forEach((scene) => {
                scene.sfx.forEach((sfx) => {
                    results.push({
                        owner: scene,
                        sfx,
                        playbackTime: scene._getSfxPlaybackTime(sfx),
                    });
                });
            });
        });

        return results.sort((a, b) => a.playbackTime - b.playbackTime);
    }

    get videoHeight() {
        return this.#videoHeight;
    }

    set videoHeight(videoHeight) {
        if (videoHeight === this.#videoHeight) {
            return;
        }
        assertNumber(videoHeight);
        assertMinimum(videoHeight, 1);
        this.#videoHeight = videoHeight;
        this.requiresUpdate = true;
    }

    get resolutionName(): string {
        const resolutionValue = getResolutionValueFromVideoHeight(
            this.videoHeight,
            this.aspectRatio,
        );
        return `${resolutionValue}p`;
    }

    #checkAndFixVideoHeight = (warn = true) => {
        // clamp the video height to the max value based on the user's capabilities
        const validVideoHeights = Config.SUPPORTED_VIDEO_HEIGHTS[this.aspectRatio];
        let userMaxResolution = validVideoHeights['480p'];
        if (this.capabilities.render720p) {
            userMaxResolution = validVideoHeights['720p'];
        }
        if (this.capabilities.render1080p) {
            userMaxResolution = validVideoHeights['1080p'];
        }
        if (!Object.values(validVideoHeights).includes(this.#videoHeight)) {
            if (warn) {
                console.warn(
                    `video height ${this.#videoHeight} not valid for aspect ratio ${this.aspectRatio}, defaulting to maximum allowed height ${userMaxResolution}`,
                );
            }
            this.#videoHeight = userMaxResolution;
        } else if (this.#videoHeight > userMaxResolution) {
            if (warn) {
                console.warn(
                    `video height ${this.#videoHeight} not valid for aspect ratio ${this.aspectRatio}, defaulting to maximum allowed height ${userMaxResolution}`,
                );
            }
            this.#videoHeight = userMaxResolution;
        }
    };

    /**
     * The renderOutput helps clarify how many render outputs (ie. videos) should be generated.
     */
    get renderOutput(): string {
        return this.#renderOutput;
    }

    set renderOutput(renderOutput) {
        if (this.renderOutput === renderOutput) {
            return;
        }
        assertString(renderOutput);
        assertInArray(renderOutput, Config.ALL_RENDER_OUTPUT_OPTIONS);
        this.#renderOutput = renderOutput;
        this.requiresUpdate = true;
    }

    #checkAndFixWatermark() {
        // if the watermark is set but capabilities.customWatermark is false, clear watermark
        if (!this.capabilities.customWatermark && this.template.watermark.sourceData) {
            // Remove watermark
            this.template.watermark.sourceData = Config.WATERMARK_NONE;
            // Hide watermark (this will hide in all scenes if a custom watermark was set)
            this.template.watermark.visible = false;
        }
    }

    get paceMultiplier() {
        return this.#paceMultiplier;
    }

    set paceMultiplier(paceMultiplier) {
        if (this.paceMultiplier === paceMultiplier) {
            return;
        }
        assertNumber(paceMultiplier);
        assertMinimum(paceMultiplier, Config.PACE_MULTIPLIER_MIN);
        assertMaximum(paceMultiplier, Config.PACE_MULTIPLIER_MAX);
        this.#paceMultiplier = paceMultiplier;
        this.requiresUpdate = true;
    }

    /**
     * The text direction. This can be left to right or right to left.
     */
    get textDirection(): TextDirection {
        return this.#textDirection;
    }

    set textDirection(textDirection) {
        if (this.textDirection === textDirection) {
            return;
        }
        assertInArray(textDirection, Config.TEXT_DIRECTIONS);
        this.#textDirection = textDirection;
        this.requiresUpdate = true;
    }

    #updateTextDirection() {
        this.sceneGroups.forEach((sceneGroup) => {
            sceneGroup._propagateTextDirection(
                this.textDirection === 'ltr' ? TextDirection.LTR : TextDirection.RTL,
            );
        });
    }

    #updateTemplate() {
        const templateProperties = this.template.save();
        this.scenes.forEach((slide) => {
            slide._template.load(templateProperties);
        });
    }

    /**
     * Returns true if the video has audio.
     */
    get hasAudio() {
        if (Object.keys(this.audioAssets).length > 0) {
            return true;
        }
        return this.sceneGroups.some((sceneGroup) => sceneGroup.hasAudio);
    }

    /**
     * @internal
     */
    _getSceneDesignConfig(sceneDesignId: string, aspectRatio: AspectRatio) {
        if (sceneDesignId.endsWith(':brand_1')) {
            return getBrandSceneDesignConfig(sceneDesignId, this.aspectRatio);
        }

        if (!this.#sceneDesigns) {
            ErrorReporter.setContext('sceneDesign', {
                sceneDesignId,
                aspectRatio,
                template: this.template.id,
            });
            throw new Error('Could not get scene design as the template bundle is not set');
        }

        if (!this.#sceneDesigns[sceneDesignId]) {
            ErrorReporter.setContext('sceneDesign', {
                sceneDesignId,
                aspectRatio,
                template: this.template.id,
            });
            throw new Error(
                'Scene design is not in the current template bundle. Pass an updated bundle to setTemplate.',
            );
        }

        return this.#sceneDesigns[sceneDesignId][aspectRatio];
    }

    #getSceneDesignIdsForCurrentTemplate(filterCategory?: string): string[] {
        if (!this.#sceneDesigns) {
            throw new Error(
                'Could not get scene design ids for current template as the template bundle is not set',
            );
        }

        if (filterCategory) {
            assertInArray(filterCategory, Config.ALL_CATEGORIES);
        }

        const firstAvailableAspectRatio: AspectRatio = (() => {
            const firstSceneDesign = Object.values(this.#sceneDesigns)[0];
            return Object.keys(firstSceneDesign).find(
                (key) => firstSceneDesign[key],
            )! as AspectRatio;
        })();

        return Object.values(this.#sceneDesigns).reduce(
            (sceneDesignIds: Array<string>, sceneDesign): string[] => {
                const sceneDesignData = sceneDesign[firstAvailableAspectRatio] as unknown as {
                    sceneDesign: string;
                    sceneCategory: SceneCategory;
                };
                const { sceneDesign: sceneDesignId, sceneCategory } = sceneDesignData;

                if (!filterCategory || sceneCategory === filterCategory) {
                    sceneDesignIds.push(sceneDesignId);
                }
                return sceneDesignIds;
            },
            [],
        );
    }

    /**
     * @internal
     */
    _selectDataFromSceneDesign<T>(
        sceneDesignId: string,
        selector: (scene: Scene) => T,
        settings?: LuminarySettings,
        aspectRatio?: AspectRatio,
    ): T {
        const dataLuminary = new Luminary({
            disableRenderer: true,
            fetchResources: false,
            ...settings,
        });

        aspectRatio = aspectRatio || this.root.aspectRatio;
        dataLuminary.aspectRatio = aspectRatio;
        dataLuminary.textDirection = this.root.textDirection;
        dataLuminary.template.load(this.root.template.save());
        try {
            const sceneDesignConfig = this.root._getSceneDesignConfig(sceneDesignId, aspectRatio);
            const scene = dataLuminary.addScene({ data: sceneDesignConfig });

            return selector(scene);
        } finally {
            void dataLuminary.destroy();
        }
    }

    /**
     * Loads a template bundle into Luminary. This operation sets the template and determines the available scene
     * designs that can be selected when creating scenes.
     */
    setTemplateBundle(templateBundle: TemplateBundle) {
        assertIsValidTemplateBundle(templateBundle);

        const migratedTemplateBundle = Migrator.updateTemplateBundle(templateBundle);

        const aspectRatio = (() => {
            const firstSceneDesign = Object.values(migratedTemplateBundle.sceneDesigns)[0];
            const currentAspectRatio = this.#aspectRatio;

            if (
                !this.canChangeTemplates ||
                !firstSceneDesign ||
                firstSceneDesign[currentAspectRatio]
            ) {
                return currentAspectRatio;
            }

            const firstAvailableAspectRatio =
                Config.getSupportedAspectRatios(migratedTemplateBundle)[0];
            return firstAvailableAspectRatio;
        })();

        this.#sceneDesigns = migratedTemplateBundle.sceneDesigns;
        this.aspectRatio = aspectRatio;

        // If the template is already set and matches, then we want to ensure the template is up to date with
        // non-destructive additions (e.g. if more fonts were added). Otherwise, we try to load the template
        // from the passed in bundle.
        const templateData = migratedTemplateBundle.templateData as RawTemplateData;
        if (templateData.id === this.template.id) {
            this.#syncTemplateAdditions(templateData);
        } else {
            this.template.load(templateData);
        }
        this.requiresUpdate = true;
    }

    /**
     * Syncs additional items from the template bundle to the video's template.
     * This handles cases where a template was updated with new fonts/colors/watermarks
     * after the video was created.
     */
    #syncTemplateAdditions(templateData: RawTemplateData) {
        if (templateData.fonts && templateData.fonts.length > this.template.fonts.length) {
            const additionalFonts = templateData.fonts.slice(this.template.fonts.length);
            this.template.fonts = [...this.template.fonts, ...additionalFonts];
        }

        if (templateData.colors && templateData.colors.length > this.template.colors.length) {
            const additionalColors = templateData.colors.slice(this.template.colors.length);
            this.template.colors = [...this.template.colors, ...additionalColors];
        }

        if (templateData.watermarkSources) {
            const existingSourceIds = new Set(
                this.template.watermarkSources.map((wm) => wm.sourceData.source_id),
            );
            const additionalWatermarks = templateData.watermarkSources.filter(
                (wm) => !existingSourceIds.has(wm.sourceData.source_id),
            );
            additionalWatermarks.forEach((wm) => this.template.addWatermarkSource(wm));
        }
    }

    /**
     * Checks if the template bundle has been set for this instance.
     */
    get isTemplateBundleSet(): boolean {
        return !!(this.template.id && this.#sceneDesigns);
    }

    /**
     * Determines if the current state allows for the template to be changed. If scenes exist
     * then luminary cannot change to using another template because we do not have a way to map
     * existing scenes from one template to another.
     */
    get canChangeTemplates(): boolean {
        if (this.sceneGroups.length === 0) {
            return true;
        }
        // if there is a branding scene then we can still change templates since that scene
        // is completely managed by luminary, but otherwise no other scenes may exist
        return !!(this.sceneGroups.length === 1 && this.hasBrandingScene);
    }

    /**
     * Gets the global template object. This cannot be set directly, but instead its properties
     * should be set. For example template.fonts = ...
     */
    override get template() {
        return this.#template;
    }

    // @deprecated
    setTemplate(templateBundle: TemplateBundle) {
        this.setTemplateBundle(templateBundle);
    }

    /**
     * Get an ordered array of time slices that represent the start time and end time for
     * a slide
     *
     * @returns an ordered list of lists indicating [startTimeMS, endTimeMS] per slide
     */
    getTimeSlices(): [number, number][] {
        return this.scenes.map((scene) => [scene.startTime, scene.endTime]);
    }

    /**
     * Get the active subscenes at the provided luminary time. Note that a subscene is no longer
     * active once the end time of the subscene is reached. This means when currentTime ===
     * luminary.duration there are no active subscenes.
     */
    #getActiveSubscenesAt(currentTime: number): Subscene[] {
        return this.getActiveScenesAt(currentTime).flatMap((scene) => {
            const sceneTime = currentTime - scene.startTime;
            return scene._getActiveSubscenesAt(sceneTime);
        });
    }

    /**
     * Get the active scenes at the provided luminary time. Note that a scene is no longer
     * active once the end time of the scene is reached. This means when currentTime ===
     * luminary.duration there are no active scenes.
     */
    getActiveScenesAt(currentTime: number): Scene[] {
        return this.getActiveSceneGroupsAt(currentTime).flatMap((sceneGroup) => {
            const sceneGroupTime = currentTime - sceneGroup.startTime;
            return sceneGroup.getActiveScenesAt(sceneGroupTime);
        });
    }

    /**
     * Get the active scene groups at the provided luminary time. Note that a scene group is no
     * longer active once the end time of the scene group is reached. This means when
     * currentTime === luminary.duration there are no active scene groups.
     * If called from `SceneGroup.play()`, transitions will be turned off between groups and the the list is further
     * filtered to remove transitional scene groups.
     */
    getActiveSceneGroupsAt(currentTime: number): SceneGroup[] {
        let sceneGroups = this._sceneGroupsTimeline
            .getActiveChildrenIndicesAt(currentTime)
            .map((index) => this.sceneGroups[index])
            .filter(Boolean);
        if (this._sceneGroupPlayConfig) {
            sceneGroups = sceneGroups.filter((sceneGroup) => {
                return (
                    sceneGroup.startTime === this._sceneGroupPlayConfig.sceneGroupRange.startTime &&
                    sceneGroup.endTime === this._sceneGroupPlayConfig.sceneGroupRange.endTime
                );
            });
        }
        return sceneGroups;
    }

    /**
     * Set the head of our timeline to the given position and line up all of our media (video and audio) assets. This
     * method should be used when rendering.
     * @param seekTime - luminary time we want to seek to in milliseconds
     * @param seekMode - this can be the either resolveWhenCanPlay or resolveWhenSingleFrame and should be chosen
     *  based on the action we want to perform once our seek has resolved. If we just want to render an image then
     *  resolveWhenSingleFrame is the right seekMode. If we want to play, then resolveWhenCanPlay is the right mode.
     */
    async seek(seekTime: number, seekMode: SeekMode = SeekMode.ResolveWhenCanPlay): Promise<void> {
        assertInEnum(seekMode, SeekMode);

        // this guarantees that first seek promise resolves before any subsequent ones
        await new Promise((resolve) => {
            setTimeout(resolve, 0);
        });

        let bufferingMode = BufferingMode.ResolveWhenSingleFrame;
        if (seekMode === SeekMode.ResolveWhenCanPlay) {
            bufferingMode = BufferingMode.ResolveWhenCanPlay;
        }
        if (this.isPlaying()) {
            bufferingMode = BufferingMode.PrepareForPlayback;
        }

        await this.#seek(seekTime, bufferingMode);

        if (this.isDrawingToCanvas) {
            await new Promise<void>((resolve) => {
                this.once('onRenderComplete', resolve);
            });
        }
    }

    /**
     * @internal
     * This is an analog of Luminary.seek, but it doesn't set timeout to guarantee sequence of seek requests.
     */
    async _seekToFrameAt(seekTime: number): Promise<void> {
        await this.#seek(seekTime, BufferingMode.ResolveWhenSingleFrame);
    }

    async #seek(seekTime: number, bufferingMode: BufferingMode): Promise<void> {
        assertNumber(seekTime);
        assertInEnum(bufferingMode, BufferingMode);

        // currentTime can get called from the render queue during destroy
        // so let's make sure the object still exists before doing any
        // work.
        if (this.destroyed) {
            return;
        }
        if (this.sceneGroups.length === 0) {
            return;
        }

        const seekMode =
            bufferingMode === BufferingMode.ResolveWhenSingleFrame
                ? SeekMode.ResolveWhenSingleFrame
                : SeekMode.ResolveWhenCanPlay;

        const bufferingPromise = async () => {
            // ensure we don't request a frame that's larger than the timeline's duration. This can
            // easily happen during playback since the time it takes to calculate a frame is variable.
            if (seekTime > this.duration) {
                seekTime = this.duration;
            }
            this.currentTime = seekTime;
            await this._propagateBuffering(bufferingMode);
            this.#seekSceneGroups(this.getActiveSceneGroupsAt(this.currentTime), seekMode);
        };

        const canFailBufferingPromise = this.#onBufferingPromise.then(bufferingPromise);
        // chain our new promise to any existing onBuffering ones
        this.#onBufferingPromise = Promise.allSettled([canFailBufferingPromise]).then(() => {});

        return canFailBufferingPromise;
    }

    /**
     * Propagate a request to buffer data down the hierarchy.
     * @internal
     */
    async _propagateBuffering(
        bufferingMode: BufferingMode = BufferingMode.ResolveWhenCanPlay,
    ): Promise<void> {
        await Promise.all(
            Object.values(this.#audioAssets).map(async (a) =>
                a._bufferAtTime(this.currentTime, bufferingMode),
            ),
        );
        await this.#bufferAllSfx(bufferingMode);

        // Pause everything, since the new time may be at a different point in the timeline
        // where some of the currently playing objects should no longer be playing
        this._propagatePause();

        // slide timeline - so we can currentTime in any local media (video) in seconds!
        const activeSceneGroups = this.getActiveSceneGroupsAt(this.currentTime);

        if (activeSceneGroups.length === 0) {
            return;
        }

        if (bufferingMode === BufferingMode.PrepareForPlayback) {
            // Propagates buffering to all slides subsequent to 0. This is required to make sure following scenes'
            // time aware assets (like videos) will start at the right time. Start with the last
            // active scene group, because it may contain inactive scenes
            const startIndex = activeSceneGroups[activeSceneGroups.length - 1].index;
            for (let i = startIndex; i < this.sceneGroups.length; i++) {
                // Do not wait for buffering to resolve.
                // Scene groups can continue loading while we start playing.
                this.sceneGroups[i]
                    ._propagateBuffering(BufferingMode.PrepareForPlayback)
                    .catch((error) => {
                        if (this.sceneGroups[i].destroyed) {
                            return;
                        }
                        ErrorReporter.capture(error, {
                            extra: {
                                action: 'Buffer future scene groups',
                            },
                        });
                    });
            }

            bufferingMode = BufferingMode.ResolveWhenCanPlay;
        }

        // sync up active scenes
        // since slides overlap during transitions, we can have 2 results
        await Promise.all(
            activeSceneGroups.map(async (sceneGroup) => {
                return sceneGroup._propagateBuffering(bufferingMode);
            }),
        );

        // It's possible we seeked to a time when some objects weren't playing (e.g. a SceneGroup
        // or AudioAsset). So if the timeline is playing we should trigger play again to make sure
        // the required objects are playing.
        if (this.isPlaying() && bufferingMode === BufferingMode.ResolveWhenCanPlay) {
            this._propagatePlay();
        }
    }

    /**
     * Get the duration of the video
     */
    get duration(): number {
        return this.timeline.duration;
    }

    /**
     * A global mute-all audio switch
     */
    get muted(): boolean {
        return this.#muted;
    }

    set muted(muted) {
        if (this.#muted === muted) {
            return;
        }
        assertBoolean(muted);
        this.#muted = muted;
        if (!this.#muted) {
            this.#resumeAudioContext();
        }
        if (this.isPlaying()) {
            // if we are playing, we need to seek to the current time
            // because the AudioAssets need to re-schedule playback and gain curves
            void this.#seek(this.currentTime, BufferingMode.PrepareForPlayback);
        }
        this.requiresUpdate = true;
    }

    override async destroy(): Promise<void> {
        if (!this._renderSceneQueue || this.destroyed) {
            console.warn('Object is already destroyed or is in the process of being destroyed.');
            return;
        }

        ErrorReporter.addBreadcrumb({
            type: 'debug',
            category: 'luminary',
            message: 'luminary destroyed',
            data: {
                name: this.#debugName,
            },
        });

        super.destroy();

        if (this.isPlaying()) {
            this.pause();
        }
        this.stopDrawingToCanvas();

        await this._renderSceneQueue.destroy();
        this._renderSceneQueue = null;

        // everything is about to be destroyed, don't try to keep things updated during destruction
        this.timeline.autoUpdate = false;
        this.autoUpdate = false;
        this.autoDraw = false;

        this.removeAllListeners();

        this._sceneGroupManager.destroy(true);

        if (this._workspace) {
            this._workspace.destroy(true);
            this._workspace = null;
        }

        if (this.timeline) {
            this.timeline.destroy();
            this.timeline = null;
        }

        if (this.canvasParent) {
            this.canvasParent = null;
        }

        this.#fontManager.off('loaded', this.#redrawOnFontLoad, this);
        this.#fontManager.off('loadedAllFallbackFonts', this.#handleAllFallbackFontsLoaded, this);

        this.#rendererManager.destroy();

        if (this._ticker) {
            this._ticker.destroy();
            this._ticker = null;
        }

        this._sceneGroupsTimeline = null;
        this.#onBufferingPromise = null;

        Object.values(this.#audioAssets).forEach((a) => {
            this.destroyAudio(a);
        });

        this._reusableTextures.destroy();

        this.#matchesBrandingCache.forEach((otherLuminary) => {
            otherLuminary.destroy();
        });
        this.#matchesBrandingCache.clear();

        this.#detachFromWindowForDebug();
    }

    get audioAssets() {
        return this.#audioAssets;
    }

    get audioContext() {
        return this.#audioContext;
    }

    set audioContext(context) {
        this.#audioContext = context;
    }

    addAudio({ id, ...settings }: AddAudioSettings) {
        if (id === undefined) {
            // first check if `data.id is set
            if (hasOwnProperty(settings, 'data') && settings.data.id) {
                id = settings.data.id as string;
            } else {
                id = uuid();
            }
        }

        assertUuid(id);

        const audioAsset = new AudioAsset({
            fetchResources: this.fetchResources,
            name: `audio_${Object.keys(this.#audioAssets).length + 1}`,
            parent: this,
            autoUpdate: false,
            id,
        });

        this.#audioAssets[audioAsset.id] = audioAsset;

        if (hasOwnProperty(settings, 'data')) {
            audioAsset.load(settings.data);
        }

        audioAsset.requiresUpdate = true;
        audioAsset.autoUpdate = this.autoUpdate;
        return audioAsset;
    }

    destroyAudio(audio: AudioAsset) {
        const id = audio.id;
        audio.destroy();
        delete this.#audioAssets[id];
    }

    /**
     * Clear all properties to their default values. This includes the removal of all scenes.
     */
    override async clear() {
        const renderQueueShouldUnlock = await (async () => {
            // renderSceneQueue may have already been removed in destroy()
            if (this._renderSceneQueue === null) {
                return false;
            }
            // clear render queues -- here we do have to wait for any running task to complete before we can clear it
            const renderQueueIsLocked = this._renderSceneQueue.locked();
            await this._renderSceneQueue.clear();
            return !renderQueueIsLocked;
        })();

        this._reusableTextures.destroy();

        this.batchRun(() => {
            // stop playback
            if (this.isPlaying()) {
                this.stop();
            }
            if (this.isDrawingToCanvas) {
                this.stopDrawingToCanvas();
            }

            // remove all scenes
            this._sceneGroupManager.clear();

            // clear capabilities
            this.#capabilities.clear();

            // clear the template
            this.template.clear();

            // reset default values
            this.aspectRatio = AspectRatio.LANDSCAPE;
            this.videoHeight = Config.SUPPORTED_VIDEO_HEIGHTS[this.#aspectRatio]['1080p'];
            this.renderOutput = Config.RENDER_OUTPUT_SINGLE_VIDEO;
            this.template.watermark.clear();
            this.paceMultiplier = Config.PACE_MULTIPLIER_DEFAULT;
            this.textDirection = TextDirection.LTR;

            // reset audio
            this.music = null;
            this.voiceover = null;
        });

        // reset queues
        if (renderQueueShouldUnlock) {
            this._renderSceneQueue.unlock();
        }
    }

    /**
     * Enable text editing for a given asset on a given scene. When enabling the asset, an HTML element will be created
     * where the user can interact with. An HTML element from the DOM should be provided, which should
     * be sitting in the same space as the canvas. All editing nodes created will be position relative
     * to this domContainer.
     */
    async enableTextEditMode(
        sceneId: string,
        textAssetId: string,
        domContainer: HTMLElement,
        options?: TextEditingOptions,
    ) {
        const scene = this.getSceneById(sceneId);
        if (!scene) {
            throw Error(`Scene ${sceneId} does not exist`);
        }

        const asset = scene.getAssetById(textAssetId) as TextAsset;
        if (!asset) {
            throw Error(`Asset ${textAssetId} does not exist`);
        }

        const subsceneIndex = scene._getSubsceneIndex(asset);
        const previewTime = scene.getPreviewTime({
            subsceneIndex: subsceneIndex === -1 ? 0 : subsceneIndex,
        });
        await this.seek(previewTime, VideoData.SeekMode.ResolveWhenSingleFrame);

        asset.enableTextInput(domContainer, options);
    }

    /**
     * @deprecated This method is a no-op wrapper. Call the callback directly instead.
     */
    restoreCursorAfterEffect(callback: Function) {
        callback();
    }

    /**
     * Disable text editing for a given asset on a given scene. When disabling the asset, the associated HTML element
     * will be removed from the DOM and destroyed.
     */
    disableTextEditMode(sceneId: string, textAssetId: string) {
        const scene = this.getSceneById(sceneId);
        if (!scene) {
            throw Error(`Scene ${sceneId} does not exist`);
        }
        const asset = scene.getAssetById(textAssetId) as TextAsset;
        if (!asset) {
            throw Error(`Asset ${textAssetId} does not exist`);
        }
        if (!asset.hasTextEditingEnabled) {
            console.warn(`Asset ${textAssetId} is not in edit mode`);
            return;
        }
        asset.disableTextInput();
    }

    /**
     * This method is called when the internal state is changed, fixed and updated. It results in
     * emitting `stateChanged` for external use.
     * @internal
     */
    override _emitChanged = (force = false) => {
        this.#debouncedStateChangedEvent(force);
    };
    /**
     * External `stateChanged` event. This event is used by our consumers to know when to re-render state. We debounce
     * the event to avoid any unnecessary update requests.
     * @param force - force the event to fire
     */
    #debouncedStateChangedEvent(force: boolean) {
        if (force) {
            this.emit('stateChanged');
            return;
        }
        if (!this.canEmitChangedEvent) {
            return;
        }
        if (this.#debounceStateChangedEvents) {
            this.#debounceEmitChanged();
        } else {
            this.emit('stateChanged');
        }
    }

    #debounceEmitChanged = debounce(
        () => {
            this.emit('stateChanged');
        },
        STATE_CHANGED_DEBOUNCE_WAIT_TIME,
        {
            maxWait: STATE_CHANGED_DEBOUNCE_MAX_WAIT_TIME,
        },
    );

    /**
     * Will render a preview at the given time (in milliseconds) to the target canvas.
     * This queues a scene image render task so if there are others processing it will have
     * to wait for those to finish first. Note that if luminary is in drawing to canvas mode,
     * this will never resolve because the queue is not processed while drawing to the canvas.
     */
    async renderAtTimeToCanvas(
        time: number,
        target: HTMLCanvasElement,
        options?: {
            mode?: SceneImageRenderMode;
        },
    ): Promise<void> {
        if (!this.renderer) {
            throw new Error('Cannot extract image when renderer is not set');
        }
        if (this.isDrawingToCanvas) {
            throw new Error('Extract image will never resolve while drawing to canvas');
        }

        const activeSubscenes = this.#getActiveSubscenesAt(time);
        if (activeSubscenes.length === 0) {
            ErrorReporter.setContext('render', { time, duration: this.duration });
            throw new Error("The provided time is not within luminary's duration");
        }
        const subscene = activeSubscenes[0];
        const subsceneTime = time - subscene.startTime;
        const subsceneRelativeTime = subsceneTime / subscene.duration;

        return this._createRenderSceneImageTask({
            sceneId: subscene.slide.id,
            subsceneIndex: subscene.index,
            relTime: subsceneRelativeTime,
            targets: [target],
            mode: options?.mode ?? DEFAULT_SCENE_IMAGE_RENDER_MODE,
        });
    }

    /**
     * Manually trigger luminary to render at the current time to its internal canvas.
     * If you want luminary to continuously render, you should use startDrawingToCanvas instead.
     */
    render() {
        if (!this.renderer) {
            return;
        }

        // start performance timing
        const activityId = uuid();
        const metricLabels = METRIC_RENDER_WORKSPACE.getLabels(
            !this._sceneGroupPlayConfig,
            this.#resolutionValue,
            this.aspectRatio,
        );

        LuminaryActivityProfiler.start(activityId);
        try {
            this.#transitionAnimateRender();
        } finally {
            LuminaryActivityProfiler.end(activityId, METRIC_RENDER_WORKSPACE.name, metricLabels);
        }
    }

    /**
     * Apply the full steps to render a frame:
     * - enable or disable transitions
     * - animate
     * - render
     */
    #transitionAnimateRender() {
        const sceneGroups = this.#prepareTransitions();
        try {
            this.#seekSceneGroups(sceneGroups, SeekMode.ResolveWhenCanPlay);
            this.#render(sceneGroups);
        } finally {
            this.#restoreTransitions(sceneGroups);
        }
    }

    #isCurrentTimeOutsideRenderRange() {
        if (this._sceneGroupPlayConfig === null) {
            return false;
        }
        return (
            this.currentTime < this._sceneGroupPlayConfig.playRange.startTime ||
            this._sceneGroupPlayConfig.playRange.endTime < this.currentTime
        );
    }

    /**
     * Renders our workspace. The workspace contains all scene groups, scenes, subscenes and assets.
     * For each frame, rendering consists of:
     * - showing the scene group workspaces to be rendered (there may be more than one if transitioning)
     * - rendering the frame - generating the pixels
     * - hiding the scene group workspaces that were rendered
     */
    #render(sceneGroups: SceneGroup[]) {
        if (!this.renderer) {
            return;
        }
        if (this.#rendererManager.webGLContextLost) {
            return;
        }
        if (this._workspace.destroyed || sceneGroups.some((sceneGroup) => sceneGroup.destroyed)) {
            return;
        }

        if (this.#isCurrentTimeOutsideRenderRange()) {
            return;
        }

        let affectedState = { sceneGroups: [], scenes: [] };

        try {
            affectedState = this.#showContainersBeforeRender(sceneGroups);

            const scaleToCanvasTransform = (() => {
                const xScale =
                    this.#canvas2d.width /
                    (this.#referenceResolution.width * this.renderer.resolution);
                const yScale =
                    this.#canvas2d.height /
                    (this.#referenceResolution.height * this.renderer.resolution);
                return new PIXI.Matrix(xScale, 0, 0, yScale, 0, 0);
            })();

            // Scope gl.viewport to this instance's output area so fragments aren't wasted on the unused shared canvas.
            this.renderer.screen.width = this.#outputResolution.width / this.renderer.resolution;
            this.renderer.screen.height = this.#outputResolution.height / this.renderer.resolution;

            // There are some render exceptions, we are trying to investigate when it happens
            // LUMINARY-37H
            try {
                this.renderer.render(this._workspace, { transform: scaleToCanvasTransform });
            } catch (e) {
                type BatchRenderer = {
                    _bufferedTextures: unknown[];
                    _bufferedElements: { _classType?: string; parent?: { _classType?: string } }[];
                };
                const batchRenderer = (
                    this.renderer as unknown as { batch: { currentRenderer: BatchRenderer } }
                ).batch.currentRenderer;
                const { _bufferedTextures, _bufferedElements } = batchRenderer;
                const batchRenderState = _bufferedElements.map((element, i) => {
                    const elementType =
                        (element &&
                            (element._classType ||
                                (element.parent && element.parent._classType))) ||
                        null;

                    return {
                        elementType,
                        element,
                        texture: _bufferedTextures[i],
                    };
                });

                ErrorReporter.setContext('batchRenderState', { batchRenderState });

                throw e;
            }

            const ctx = this.#canvas2d.getContext('2d');
            ctx.clearRect(0, 0, this.#canvas2d.width, this.#canvas2d.height);
            ctx.drawImage(
                this.renderer.view,
                0,
                0,
                this.#canvas2d.width,
                this.#canvas2d.height,
                0,
                0,
                this.#canvas2d.width,
                this.#canvas2d.height,
            );
        } finally {
            this.#hideContainersAfterRender(affectedState);
            this._reusableTextures.detachTextures();
        }
    }

    /**
     * Animates the given scene groups.
     *
     * All aspect of animations (curve progress, calling overloaded animation methods, transitions, etc.) are applied.
     */
    #seekSceneGroups(sceneGroups: SceneGroup[], seekMode: SeekMode) {
        sceneGroups.forEach((sceneGroup) => sceneGroup.seek(seekMode));
    }

    #prepareTransitions() {
        const sceneGroups = this.getActiveSceneGroupsAt(this.currentTime);
        if (this._sceneGroupPlayConfig) {
            // Since we are playing a scene group, we need to turn off transitions on the group boundaries.
            sceneGroups.forEach((sceneGroup) => {
                const ixLast = sceneGroup.scenes.length - 1;
                if (ixLast === -1) return;
                sceneGroup.scenes[0].customTransition.hideTransition();
                // The transition manager knows about the next transition and will apply it. Turn it off!
                if (sceneGroup.scenes[ixLast].nextScene) {
                    sceneGroup.scenes[ixLast].nextScene.customTransition.hideTransition();
                }
            });
        }
        return sceneGroups;
    }

    #restoreTransitions(sceneGroups: SceneGroup[]) {
        if (this._sceneGroupPlayConfig) {
            sceneGroups.forEach((sceneGroup) => {
                const ixLast = sceneGroup.scenes.length - 1;
                if (ixLast === -1) return;
                sceneGroup.scenes[0].customTransition.showTransition();
                if (sceneGroup.scenes[ixLast].nextScene) {
                    sceneGroup.scenes[ixLast].nextScene.customTransition.showTransition();
                }
            });
        }
    }

    #showContainersBeforeRender(sceneGroups: SceneGroup[]): {
        sceneGroups: SceneGroup[];
        scenes: Scene[];
    } {
        const scenes = [];
        sceneGroups.forEach((sceneGroup) => {
            sceneGroup._workspace._pixiContainer.visible = true;
            const time = this.currentTime - sceneGroup.startTime;
            sceneGroup._updateForRender();
            sceneGroup.getActiveScenesAt(time).forEach((scene) => {
                scenes.push(scene);
                scene.sceneWorkspace._pixiContainer.visible = true;
                scene.updateForRender();
            });
        });

        return { sceneGroups, scenes };
    }

    #hideContainersAfterRender({
        sceneGroups,
        scenes,
    }: {
        sceneGroups: SceneGroup[];
        scenes: Scene[];
    }) {
        sceneGroups.forEach((sceneGroup) => {
            sceneGroup._workspace._pixiContainer.visible = false;
        });
        scenes.forEach((scene) => {
            scene.sceneWorkspace._pixiContainer.visible = false;
        });
    }

    /**
     * This method is registered to the PIXI ticker and gets called every tick or update, ensuring all workspace
     * changes are rendered.
     * @param time - the time we're rendering at. This argument is passed along by the ticker.
     */
    #renderTickerUpdate(_time: number) {
        if (this.destroyed) {
            return;
        }

        try {
            this.#transitionAnimateRender();
        } catch (e) {
            ErrorReporter.capture(e, {
                extra: {
                    renderAll: !this._sceneGroupPlayConfig,
                },
            });
        }

        this.emit('onRenderComplete');
    }

    /**
     * Add a task to the render queue to render the given scene's subscenes
     * @internal
     */
    async _createRenderSceneImageTask(options: RenderSceneImageTask): Promise<void> {
        const scene = this.getSceneById(options.sceneId);
        if (!scene) {
            return;
        }

        return this._renderSceneQueue.enqueue(options);
    }

    /**
     * Renders the specified subscene
     */
    #renderSceneImageTask(
        work: RenderSceneImageTask,
        done: (options: TaskCallbackOptions) => void,
    ) {
        this.#runRenderSceneImageTask(work)
            .then((canvases) => {
                done(canvases);
                if (canvases.length > 0) {
                    canvases.forEach((canvas) => {
                        this.emit('onRenderedSlideImage', work.sceneId, work.subsceneIndex, canvas);
                    });
                } else {
                    this.emit('onRenderedSlideImage', work.sceneId, work.subsceneIndex, null);
                }
            })
            .catch((error) => done(error));
    }

    async #runRenderSceneImageTask(work: RenderSceneImageTask): Promise<RenderSceneImageResult[]> {
        // Render the given scene and the given relative time
        let scene = this.getSceneById(work.sceneId);
        if (!scene || scene.destroyed) {
            return [];
        }
        if (this.isPlaying()) {
            console.warn(
                `Unable to render slide image for ${work.sceneId} since we're still playing`,
            );
            this.stop();
        }
        if (this.isDrawingToCanvas) {
            ErrorReporter.setContext('#runRenderSceneImageTask', { sceneId: work.sceneId });
            throw Error(`Unable to render slide image since we're still drawingToCanvas`);
        }

        const currentTime = scene._getLuminaryTimeAtSceneProgress(work.relTime);

        await this.#seek(currentTime, BufferingMode.ResolveWhenSingleFrame);

        if (work.mode === SceneImageRenderMode.ALL_ASSETS_VISIBLE) {
            scene._seekAssetTimelinesToAnimationMidpoint(work.subsceneIndex);
        }

        scene = this.getSceneById(work.sceneId);
        if (!scene || scene.destroyed) {
            // scene was removed/destroyed during seeking, nothing more to do
            return [];
        }

        this.#render(this.getActiveSceneGroupsAt(this.currentTime));

        // extract image data
        if (!work.targets) {
            return [this.#extractImageFromRenderer(undefined)];
        } else {
            return work.targets.map((target) => {
                return this.#extractImageFromRenderer(target);
            });
        }
    }

    #extractImageFromRenderer(canvas?: HTMLCanvasElement): RenderSceneImageResult {
        if (!this.renderer) {
            throw new Error('Renderer not found');
        }
        if (!canvas) {
            canvas = document.createElement('canvas');
            canvas.width = this.#outputResolution.width;
            canvas.height = this.#outputResolution.height;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            // Check this since canvas can be passed in externally
            console.warn('Unable to get canvas context');
            return canvas;
        }
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(
            this.renderer.view,
            0,
            0,
            this.#outputResolution.width,
            this.#outputResolution.height,
            0,
            0,
            canvas.width,
            canvas.height,
        );
        return canvas;
    }

    async renderAudio(startTime: number, stopTime: number) {
        console.log(`renderAudio startTime=${startTime} stopTime=${stopTime}`);
        if (!(this.#audioContext instanceof OfflineAudioContext)) {
            throw new Error(
                'You can only renderAudio with an OfflineAudioContext. Please call setUpOfflineContext first!',
            );
        }
        assertMinimum(startTime, 0);
        assertNumberBetween(stopTime, startTime, this.duration, true);
        console.log('renderAudio start seek');
        await this.#seek(startTime, BufferingMode.PrepareForPlayback);
        console.log('renderAudio seek done');
        Object.values(this.audioAssets).forEach((a) => a._propagatePlay());
        this.#scheduleAllSfxPlayback(startTime);
        this.sceneGroups.forEach((sceneGroup) => {
            if (sceneGroup.mediaAsset?.hasAudio) {
                sceneGroup.mediaAsset._audioAsset.startDelay = sceneGroup.startTime - startTime;
                sceneGroup.mediaAsset._audioAsset._propagatePlay();
            }
            if (sceneGroup.voiceoverAsset) {
                sceneGroup.voiceoverAsset.startDelay = sceneGroup.startTime - startTime;
                sceneGroup.voiceoverAsset._propagatePlay();
            }
            sceneGroup.scenes.forEach((scene) => {
                scene
                    .getAssetsByType(Config.MEDIA_ASSET_TYPE, { deep: true })
                    .forEach((mediaAsset) => {
                        if (mediaAsset.hasAudio && !mediaAsset.linkToSceneGroup) {
                            mediaAsset._audioAsset.startDelay = scene.startTime - startTime;
                            mediaAsset._audioAsset._propagatePlay();
                        }
                    });
            });
        });
        console.log('renderAudio start render');
        return this.#audioContext.startRendering();
    }

    setUpOfflineContext(startTime: number, stopTime: number) {
        if (this.#audioMode !== Config.AUDIO_MODES.RENDER) {
            throw new Error('offline audio context can only be used in RENDER mode');
        }

        const actualStopTime = isNaN(stopTime) ? this.duration : stopTime;
        const renderDuration = actualStopTime - startTime;
        const seconds = renderDuration / 1000.0;
        const bufferLength = Math.round(Config.AUDIO_RENDER_SAMPLE_RATE * seconds);
        this.#audioContext = new OfflineAudioContext(
            Config.AUDIO_RENDER_NUMBER_CHANNELS,
            bufferLength,
            Config.AUDIO_RENDER_SAMPLE_RATE,
        );
    }

    /**
     * The current API version
     */
    get version(): string {
        return VERSION;
    }

    #checkAndFixTransition() {
        if (this.renderOutput === Config.RENDER_OUTPUT_VIDEO_PER_SLIDE) {
            this.template.transitionsEnabled = false;
        }
    }

    /**
     * @internal
     */
    override _checkAndFixProperties() {
        super._checkAndFixProperties();
        this.#checkAndFixVideoHeight(false);
        this.#checkAndFixResolution();
        this.#checkAndFixWatermark();
        this.#checkAndFixTransition();
        this._sceneGroupManager._checkAndFixProperties();
    }

    /**
     * Run tasks in batch by setting autoUpdate and autoDraw to false temporarily
     */
    batchRun<T>(callback: () => T): T {
        const initCanEmitChangedEvent = this.canEmitChangedEvent;
        const initAutoUpdate = this.autoUpdate;
        const initAutoDraw = this.autoDraw;
        this.canEmitChangedEvent = false;
        this.autoUpdate = false;
        this.autoDraw = false;

        try {
            return callback();
        } finally {
            this.canEmitChangedEvent = initCanEmitChangedEvent;
            this.autoUpdate = initAutoUpdate;
            this.autoDraw = initAutoDraw;
        }
    }

    /**
     * @internal
     */
    override _update() {
        this.batchRun(() => {
            this.#updateAspectRatio();
            this.#updateCanvasSize();
            this.#updateTemplate();
            this.#updateTextDirection();
            this._updateTransition();
            this.#updateDurationBasedOnMediaAsset();
            MorphManager.updateMorphAnimations(this.scenes);
            this.#updateSceneGroupManager();
            this.#updateAssetColorGradingProperties();

            // Update the audio animation manager, this should happen after all scenes have been updated since
            // the audio animations depend on scene properties.
            this._updateAudioAnimationManager();
        });
    }

    /**
     * @internal
     */
    _updateTransition() {
        this.batchRun(() => {
            this._sceneGroupManager._traverseScenes((scene, sceneIndex, sceneGroup) => {
                const currSceneTransitionManager = scene._transitionManager;

                function resetSceneTimelineOffset() {
                    scene._timeline.offset = 0;
                    if (sceneGroup.scenes[0] === scene) {
                        sceneGroup._timeline.offset = 0;
                    }
                }

                const isVideoTransitionsEnabled = this.template.transitionsEnabled;
                const isSceneTransitionEnabled = scene.transitionEnabled;

                // If video level transitions are disabled,
                // remove in-transition and out-transition.
                if (!isVideoTransitionsEnabled) {
                    currSceneTransitionManager.removeInTransition();
                    currSceneTransitionManager.removeOutTransition();
                    resetSceneTimelineOffset();
                    return;
                }

                const prevScene = scene.prevScene;
                const nextScene = scene.nextScene;
                const hasPrevScene = !!prevScene;
                const hasNextScene = !!nextScene;
                const isFirstScene = !hasPrevScene;
                const isLastScene = !hasNextScene;

                // This is the only scene.
                // Remove in-transition and out-transition.
                const isOnlyScene = isFirstScene && isLastScene;
                if (isOnlyScene) {
                    currSceneTransitionManager.removeInTransition();
                    currSceneTransitionManager.removeOutTransition();
                    resetSceneTimelineOffset();
                    return;
                }

                // This is not the only scene i.e. there are at least 2 scenes.
                // In this case, remove in-transition only for first scene.
                // The out-transition will be removed by the next scene.
                if (isFirstScene) {
                    currSceneTransitionManager.removeInTransition();
                    resetSceneTimelineOffset();
                    return;
                }

                // This is the last scene.
                // Remove out-transition as there is no next scene available to do this.
                // Don't continue because we may have to remove in-transition also.
                if (isLastScene) {
                    currSceneTransitionManager.removeOutTransition();
                }

                // if the scene level transition has been toggled off,
                // remove in transition for current scene, and out for previous.
                if (!isSceneTransitionEnabled) {
                    currSceneTransitionManager.removeInTransition();
                    prevScene._transitionManager.removeOutTransition();
                    resetSceneTimelineOffset();
                    return;
                }

                // Scene has a transition manager and this is not the first scene either.
                // So there surely is a transition manager on the previous scene.
                const prevSceneTransitionManager = prevScene._transitionManager;

                const preScaleTransitionDuration =
                    currSceneTransitionManager.extractedData.transitionDuration;
                const preScaleoverlapDuration =
                    currSceneTransitionManager.extractedData.overlapDuration;
                const preScaleInTransitionDuration =
                    currSceneTransitionManager.extractedData.inTransitionDuration;
                const preScaleInTransitionOffset =
                    currSceneTransitionManager.extractedData.inTransitionStartTime;
                const preScaleOutTransitionDuration =
                    currSceneTransitionManager.extractedData.outTransitionDuration;
                const preScaleOutTransitionOffset =
                    currSceneTransitionManager.extractedData.outTransitionStartTime;

                const scaleFactor = scene._calculateTransitionScaleFactor();

                const transitionDuration = preScaleTransitionDuration * scaleFactor;
                const overlapDuration = Math.round(preScaleoverlapDuration * scaleFactor);
                const inTransitionDuration = Math.round(preScaleInTransitionDuration * scaleFactor);
                const inTransitionOffset = Math.round(preScaleInTransitionOffset * scaleFactor);
                const outTransitionDuration = Math.round(
                    preScaleOutTransitionDuration * scaleFactor,
                );
                const outTransitionOffset = Math.round(preScaleOutTransitionOffset * scaleFactor);

                // Overlap the scene timelines.
                // For the first scene within a scene group we offset the scene group timeline,
                // so transitions happen between groups; otherwise we use the scene timeline
                if (sceneGroup.scenes[0] === scene) {
                    sceneGroup._timeline.offset = -overlapDuration;
                } else {
                    scene._timeline.offset = -overlapDuration;
                }

                // Update transition manager on current and previous scene.
                // This is required for seeking and animating all the child Lottie assets.
                currSceneTransitionManager.inTransitionDuration = inTransitionDuration;
                currSceneTransitionManager.inTransitionStartTime = inTransitionOffset;
                prevSceneTransitionManager.outTransitionDuration = outTransitionDuration;
                prevSceneTransitionManager.outTransitionStartTime = outTransitionOffset;

                // Before we can add color transition, we need to get color maps.
                // We do color mapping of color transition assets here and not in transition manager
                // because it is here, in this function where we add the color transition Lottie assets.
                // To color map partials in TransitionManager, we need to create AnimationData instance for each partial.
                // Lottie asset already creates this instance for rendering and it also knows how to color map Lottie JSON.
                // We simply add the Lottie asset, load the required color maps and let Lottie asset do the rest.
                const colorMaps = currSceneTransitionManager._transitionColorMapsCustomSave(
                    true,
                    true,
                    true,
                    false,
                );
                currSceneTransitionManager._applyInTransition(
                    currSceneTransitionManager.extractedData,
                    transitionDuration,
                    inTransitionDuration,
                    colorMaps,
                );
                prevSceneTransitionManager._applyOutTransition(
                    currSceneTransitionManager.extractedData,
                    transitionDuration,
                    outTransitionDuration,
                    colorMaps,
                );
            });
        });
    }

    #updateDurationBasedOnMediaAsset() {
        this.sceneGroups.forEach((sceneGroup) => {
            sceneGroup._updateDurationBasedOnMediaAsset();
        });
    }

    /**
     * Returns a list of scene objects in the order they're configured in the video.
     */
    get scenes(): Scene[] {
        return this._sceneGroupManager.scenes;
    }

    /**
     * Map a function over all scenes from every group. This is like performing Array.map for scenes,
     * but the result will be a nested array, organized per scene group.
     *
     * This method gives access to all scenes and their parent group in one O(N) operation.
     */
    traverseScenes<T>(callback: TraverseScenesCallback<T>) {
        return this._sceneGroupManager._traverseScenes(callback);
    }

    getAssets(options: GetAssetsOptions = { deep: true }): Asset[] {
        return this.sceneGroups.flatMap((sceneGroup) => sceneGroup.getAssets(options));
    }

    /**
     * Get all children of the given classType in our hierarchy.
     */
    getAssetsByType<T extends AssetType>(
        classType: T,
        options?: GetAssetsOptions,
    ): GetAssetType<T>[];
    getAssetsByType(classType: AssetType, options: GetAssetsOptions = { deep: true }) {
        return this.sceneGroups.flatMap((sceneGroup) =>
            sceneGroup.getAssetsByType(classType, options),
        );
    }

    /**
     * Register an asset in the global index for O(1) lookups.
     * @internal
     */
    _registerAsset(asset: Asset): void {
        // Only register if this ID is not already taken
        // This prevents load() from overwriting existing assets with duplicate IDs
        // (which happens temporarily before generateNewAssetIds() is called)
        if (!this.#assetsById.has(asset.id)) {
            this.#assetsById.set(asset.id, asset);
        }
    }

    /**
     * Unregister an asset from the global index.
     * @internal
     */
    _unregisterAsset(assetId: string): void {
        this.#assetsById.delete(assetId);
    }

    /**
     * Update asset ID in the global index when ID changes.
     * Maintains old ID mapping for backward compatibility with code that holds stale references.
     * @internal
     */
    _updateAssetId(oldId: string, newId: string, asset: Asset): void {
        // Only update the index if this asset was actually registered under the old ID
        // This prevents duplicated assets from interfering with the original's ID
        const existingAsset = this.#assetsById.get(oldId);

        if (existingAsset === asset) {
            // This asset owns the old ID, so we can migrate it to the new ID
            this.#assetsById.delete(oldId);
            this.#assetsById.set(newId, asset);

            // Track old-to-new ID mapping for backward compatibility
            this.#oldToNewIdMap.set(oldId, newId);
        } else {
            // This asset doesn't own the old ID (probably a duplicate), just register under new ID
            this.#assetsById.set(newId, asset);
        }
    }

    getAssetById(assetId: string, _options?: GetAssetsOptions): Asset | undefined {
        // O(1) lookup using global asset index
        let currentId = assetId;
        let result = this.#assetsById.get(currentId);

        // If not found, check if this is an old ID that was remapped
        // Follow the chain of old-to-new mappings to find the current ID
        if (!result) {
            const seenIds = new Set<string>();
            while (this.#oldToNewIdMap.has(currentId) && !seenIds.has(currentId)) {
                seenIds.add(currentId);
                currentId = this.#oldToNewIdMap.get(currentId)!;
                result = this.#assetsById.get(currentId);
                if (result) break;
            }
        }

        return result;
    }

    /**
     * Returns the list of scene groups. Note that this list should not be modified other than through the Luminary
     * API or the SceneGroup methods.
     */
    get sceneGroups(): SceneGroup[] {
        return this._sceneGroupManager._sceneGroups;
    }

    /**
     * Returns the scene in the current Luminary instance with that ID.  If no matching scene is found,
     * returns `undefined`.
     */
    getSceneById(id: string): Scene | undefined {
        return this._sceneGroupManager._getSceneById(id);
    }

    /**
     * Get a scene group using its id.
     */
    getSceneGroupById(sceneGroupId: string): SceneGroup {
        return this._sceneGroupManager._getSceneGroupById(sceneGroupId);
    }

    /**
     * Adds a new scene
     */
    addScene(settings: AddSceneSettings): Scene {
        const { scene } = this._sceneGroupManager._addSceneGroup(settings);
        return scene;
    }

    /**
     * Adds a SceneGroup with one scene and returns both.
     */
    addSceneGroup(settings: AddSceneSettings): { sceneGroup: SceneGroup; scene: Scene } {
        return this._sceneGroupManager._addSceneGroup(settings);
    }

    /**
     * Move a group (identified by its SceneGroupId) to a new index.
     */
    moveSceneGroupToIndex(sceneGroupId: string, newIndex: number) {
        this._sceneGroupManager._moveSceneGroupToIndex(sceneGroupId, newIndex);
    }

    /**
     * Remove a scene group by id (Removes all scenes from group in Luminary).
     */
    removeSceneGroup(sceneGroupId: string) {
        this._sceneGroupManager._removeSceneGroup(sceneGroupId);
    }

    /**
     * Removes all scene groups, removing their scenes and freeing their resources.
     */
    removeAllSceneGroups() {
        this._sceneGroupManager._removeAllSceneGroups();
    }

    /**
     * Creates a scene group at the end of the video containing an outro scene
     * (before branding scene if present). If one already exists, do nothing.
     */
    enableOutroSceneGroup({
        sceneDesignId,
    }: {
        /**
         * id to use for the scene if creating a new scene
         */
        sceneDesignId: string;
    }): { sceneGroup: SceneGroup; scene: Scene } | undefined {
        const { sceneGroup, scene } = this.getOutroSceneGroup() || {};
        if (sceneGroup) {
            return { sceneGroup, scene };
        }

        const outroSceneDesignsIds = this.#getSceneDesignIdsForCurrentTemplate(
            Config.CATEGORY_OUTRO,
        );

        assertInArray(sceneDesignId, outroSceneDesignsIds);
        // add new slide to the end
        return this.addSceneGroup({
            sceneDesignId,
        });
    }

    /**
     * Deletes the scene group containing the outro scene, if present.
     */
    disableOutroSceneGroup() {
        const { sceneGroup: outroSceneGroup } = this.getOutroSceneGroup() || {};
        if (!outroSceneGroup) {
            // doesn't exist!
            return;
        }
        this.removeSceneGroup(outroSceneGroup.id);
    }

    getOutroSceneGroup(): { sceneGroup: SceneGroup; scene: Scene } | undefined {
        const sceneGroup = this.sceneGroups.find((sceneGroup) =>
            sceneGroup.scenes.some((scene) => scene.sceneCategory === Config.CATEGORY_OUTRO),
        );
        if (sceneGroup) {
            return { sceneGroup, scene: sceneGroup.scenes[0] };
        }
    }

    get hasBrandingScene(): boolean {
        return this.scenes.some((scene) => scene.sceneCategory === Config.CATEGORY_BRAND);
    }

    override get propertiesToSerialize(): string[] {
        return [
            'audioAssets',
            'musicId',
            'voiceoverId',
            'sfxVolume',
            'aspectRatio',
            'videoHeight',
            'paceMultiplier',
            'textDirection',
            'renderOutput',
            'capabilities',
            'template',
        ];
    }

    override get readonlyProperties() {
        return super.readonlyProperties.concat(['version']);
    }

    query(queryString: string) {
        return query(this, queryString);
    }

    /**
     * Allows to get data about a scene design without creating a scene hierarchy.
     */
    getSceneDesignInfo(sceneDesignId: string): { sceneCategory: SceneCategory } {
        const { sceneCategory } = this._getSceneDesignConfig(sceneDesignId, this.aspectRatio);

        return {
            sceneCategory,
        };
    }

    async generateCaptionsString(format: 'srt' | 'vtt'): Promise<string> {
        const chunks: string[] = [];
        for (const sceneGroup of this.sceneGroups) {
            const captionAsset = sceneGroup.captions.asset;
            const offset = sceneGroup.startTime;
            const generate =
                format === 'vtt'
                    ? captionAsset.generateVTTText.bind(captionAsset)
                    : captionAsset.generateSRTText.bind(captionAsset);
            chunks.push(
                ...(await generate(
                    offset,
                    sceneGroup.voicedrivenAssetPlaybackRegions,
                    sceneGroup.voicedrivenAssetDuration,
                )),
            );
        }
        if (chunks.length === 0) {
            return '';
        }
        if (format === 'vtt') {
            return ('WEBVTT\n\n' + chunks.join('\n\n')).trim();
        }
        return chunks
            .reduce((acc, chunk, index) => {
                return acc + (index + 1) + '\n' + chunk + '\n\n';
            }, '')
            .trim();
    }

    /**
     * Use this method if you need to debug pixi objects with https://github.com/bfanger/pixi-inspector
     */
    activatePixiDevtools() {
        window.__PIXI_STAGE__ = this._workspace;
        window.__PIXI_RENDERER__ = this.renderer;
    }

    getVirtualSceneDesigns(templateBundle: TemplateBundle, userOverrides: UserOverrides) {
        return getVirtualSceneDesigns({ luminary: this, templateBundle, userOverrides });
    }
}

registerDevtoolsFormatters();

export {
    FrameByFrameRenderer,
    getGlobalFontManager,
    RenderFlags,
    OfflineAudioEncoder,
    assetNameComponentsFromName,
    assetNameFromNameComponents,
    VideoData,
    RawVideoData,
    OpaqueVideoData,
    MediaSourceData,
    AudioSourceData,
    IconSourceData,
    AssetGroup,
    Scene,
    Subscene,
    SceneSettings,
    LicenseEnum,
    Milliseconds,
    TimeAwareMediaAsset,
    IconAsset,
    Asset,
    SceneGroup,
    ColorAsset,
    GradientAsset,
    FancyCaptionAsset,
    TemplateColor,
    TextEditingOptions,
    TemplateBundle,
    BaseGroup,
    AspectRatio,
    TextDirection,
    DynamicStylingCreationLevel,
    DynamicLevel,
    AssetType,
    GetAssetType,
    SceneCategory,
    TextAssetDynamicAnimation,
    TextAssetDynamicGraphic,
    TextAssetDynamicBox,
    TextAssetDynamicBoxBorder,
    TextAssetStyle,
    DynamicAnimationConfiguration,
    DynamicGraphicsConfigurator,
    DynamicStyleData,
    GraphicClassTypes,
    TranscriptData,
    WatermarkPositions,
    TemplateWatermarkSource,
    ColorChannelMapFilter,
    BlurFilter,
    DuoToneFilter,
    GreyscaleFilter,
    SceneTemplateReferenceMap,
    TranscriptWordData,
    NumberArray,
    BaseTimeline,
    BaseAsset,
    LottieAsset,
    AudioAssetType as AudioAsset,
    PolygonMask,
    AddSceneSettings,
    TemplateType as Template,
    AlignChildren,
    TextAlignment,
    Resolution,
    LuminaryObject,
    MediaSize,
    TemplateColorProperties,
    TextAsset,
    BoundingBox,
    DynamicGraphicAsset,
    ColorMap,
    StencilMask,
    Filter,
    AssetTemplateReferenceMap,
    CaptionAsset,
    GetAssetsOptions,
    CopyPasteData,
    RawSceneData,
    Migrator,
    SceneAnnotation,
    DynamicInferredHeight,
    renderMp4,
    renderWebm,
    ResourcesStats,
    AssetCreatedFrom,
    WatermarkLayout,
    AssetSuffix,
    SceneSfxEvent,
    AssetSfxEvent,
    RectangleAsset,
    PlaybackRegion,
};
